/**
 * Elite Batch 6 Part 1 — Workflow Templates 448–467
 * Order Shipping, Delivery, PDP Recovery, Payment, Personal Shopper,
 * Recommendations, Pinterest, Post-Purchase, Price Management, Analytics, Availability
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function pushNode(
  id: string,
  label: string,
  y: number,
  title: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "pushNotification",
    label,
    y,
    { pushNotificationDetails: { title, body } },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function postmarkNode(
  id: string,
  label: string,
  y: number,
  to: string,
  templateAlias: string,
  templateModel: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.postmarkapp.com/email/withTemplate",
        headers: [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "From", value: "{sender_email}" },
          { key: "To", value: to },
          { key: "TemplateAlias", value: templateAlias },
          { key: "TemplateModel", value: templateModel },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function pagerdutyNode(
  id: string,
  label: string,
  y: number,
  summary: string,
  severity: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://events.pagerduty.com/v2/enqueue",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [
          { key: "routing_key", value: "{pagerduty_routing_key}" },
          { key: "event_action", value: "trigger" },
          {
            key: "payload",
            value: `{"summary":"${summary}","severity":"${severity}","source":"omni-workflow"}`,
          },
        ],
        variables: [
          { name: "dedup_key", customName: "pd_dedup_key", type: "string" },
          { name: "status", customName: "pd_status", type: "string" },
        ],
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 6 PART 1 — TEMPLATES 448–467
// =============================================================================

export const ELITE_BATCH6_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 448. ORDER SHIPPED NOTIFICATION (8 nodes)
  // ===================================================================

  "Order Shipped Notification": {
    summary:
      "Sends multi-channel shipped notifications with carrier tracking. A Shopify fulfillment webhook triggers AI extraction of order and shipment details, pulls full order and tracking info from Shopify, resolves carrier name and ETA via runCode, routes on delivery speed, sends a transactional shipped email via Postmark, delivers SMS tracking via Twilio, alerts ops on Slack for any delivery failures, and logs retries to Sheets.",
    nodes: [
      trigger("Webhook: Shopify fulfillment created", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A Shopify fulfillment is created when an order ships, containing tracking number, carrier, and line items",
        setup:
          "Configure Shopify webhook for fulfillments/create. Set up Postmark transactional template for shipping. Provide Twilio credentials for SMS.",
        inputFields: [],
      }),

      // 2. AI extraction of fulfillment payload
      aiNode(
        "2",
        "AI extract fulfillment details",
        280,
        "You are a Shopify fulfillment data parser. Extract the order ID, fulfillment ID, tracking number, tracking URL, carrier name, customer email, customer phone, customer first name, shipping address city, shipping address country, line item names, and fulfillment status from the webhook payload regardless of format. Respond JSON: {order_id: string, fulfillment_id: string, tracking_number: string, tracking_url: string, carrier: string, customer_email: string, customer_phone: string, customer_name: string, ship_city: string, ship_country: string, line_items: string, fulfillment_status: string}",
        "{webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "fulfillment_id", as: "fulfillment_id", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "tracking_url", as: "tracking_url", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ship_city", as: "ship_city", type: "string" },
          { field: "ship_country", as: "ship_country", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          {
            field: "fulfillment_status",
            as: "fulfillment_status",
            type: "string",
          },
        ],
      ),

      // 3. Order + shipment pull from Shopify
      shopifyNode(
        "3",
        "Pull full order and shipment details",
        520,
        "GET",
        "orders/{order_id}.json?fields=id,name,created_at,total_price,shipping_lines,shipping_address",
        [],
        [
          { name: "order.name", customName: "order_name", type: "string" },
          {
            name: "order.total_price",
            customName: "order_total",
            type: "string",
          },
          {
            name: "order.shipping_lines[0].title",
            customName: "shipping_method",
            type: "string",
          },
        ],
      ),

      // 4. Carrier + ETA resolution (runCode)
      n("4", "runCode", "Resolve carrier and ETA", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Resolve carrier display name and estimated delivery window
const carrierMap = {
  "usps": { name: "USPS", domestic_days: "3-5", intl_days: "7-21" },
  "ups": { name: "UPS", domestic_days: "2-5", intl_days: "5-14" },
  "fedex": { name: "FedEx", domestic_days: "2-5", intl_days: "4-10" },
  "dhl": { name: "DHL", domestic_days: "3-7", intl_days: "3-8" },
  "canada_post": { name: "Canada Post", domestic_days: "3-8", intl_days: "7-21" },
  "royal_mail": { name: "Royal Mail", domestic_days: "2-5", intl_days: "7-14" },
};
const rawCarrier = (vars.carrier || "").toLowerCase().replace(/[^a-z_]/g, "");
const match = carrierMap[rawCarrier] || { name: vars.carrier || "Carrier", domestic_days: "3-7", intl_days: "7-21" };
const isInternational = (vars.ship_country || "").toUpperCase() !== "US" && (vars.ship_country || "").toUpperCase() !== "UNITED STATES";
const etaDays = isInternational ? match.intl_days : match.domestic_days;
const shippingMethod = vars.shipping_method || "Standard";
const isExpress = shippingMethod.toLowerCase().includes("express") || shippingMethod.toLowerCase().includes("priority") || shippingMethod.toLowerCase().includes("overnight");
const result = {
  carrier_display: match.name,
  eta_window: etaDays + " business days",
  delivery_speed: isExpress ? "express" : "standard",
  is_international: isInternational ? "yes" : "no",
  tracking_link: vars.tracking_url || "https://track.aftership.com/" + (vars.tracking_number || "")
};
return result;`,
          outputVariables: [
            { name: "carrier_display", type: "string" },
            { name: "eta_window", type: "string" },
            { name: "delivery_speed", type: "string" },
            { name: "is_international", type: "string" },
            { name: "tracking_link", type: "string" },
          ],
        },
      }),

      // 5. Delivery speed condition
      n("5", "condition", "Express delivery?", 1000, {
        conditionData: [
          {
            variable: "delivery_speed",
            value: "express",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Shipped email via Postmark (transactional)
      postmarkNode(
        "6",
        "Postmark: Shipped notification email",
        1240,
        "{customer_email}",
        "order-shipped",
        `{"name":"{customer_name}","order_number":"{order_name}","carrier":"{carrier_display}","tracking_number":"{tracking_number}","tracking_url":"{tracking_link}","eta":"{eta_window}","items":"{line_items}","city":"{ship_city}","total":"{order_total}"}`,
        [
          {
            name: "MessageID",
            customName: "postmark_message_id",
            type: "string",
          },
          {
            name: "ErrorCode",
            customName: "postmark_error_code",
            type: "number",
          },
        ],
      ),

      // 7. SMS tracking via Twilio
      smsNode(
        "7",
        "Twilio: SMS tracking notification",
        1480,
        "{customer_phone}",
        "Hi {customer_name}! Your order {order_name} has shipped via {carrier_display}. Track it here: {tracking_link} — Estimated delivery: {eta_window}",
      ),

      // 8. Failure Slack alert + retry logging
      slackNode(
        "8",
        "Slack: Delivery ops failure alert",
        1720,
        ":warning: Shipping Notification Alert\\nOrder: {order_name} | Total: ${order_total}\\nCarrier: {carrier_display} | Tracking: {tracking_number}\\nETA: {eta_window} | Speed: {delivery_speed}\\nPostmark status: {postmark_error_code}\\nCustomer: {customer_email}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // express -> shipped email
      e("5", "6", "output_2"), // standard -> same shipped email (both paths)
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 449. OUT-FOR-DELIVERY NOTIFICATION (7 nodes)
  // ===================================================================

  "Out-for-Delivery Notification": {
    summary:
      "Sends real-time out-for-delivery alerts across preferred channels. A carrier status webhook triggers dedup and ETA enrichment via runCode, checks channel preference, sends push notification via Firebase, falls back to email if push is unavailable, logs failures, and alerts delivery ops on Slack.",
    nodes: [
      trigger("Webhook: Carrier out-for-delivery status", "webhook_app", {
        source: "AfterShip",
        event: "tracking/update",
        howItStarts:
          "A carrier tracking webhook fires when a shipment status changes to out-for-delivery, containing tracking number, carrier, and estimated delivery time",
        setup:
          "Configure AfterShip or carrier webhook for out-for-delivery status updates. Set up Firebase Cloud Messaging credentials. Provide email fallback template.",
        inputFields: [],
      }),

      // 2. Dedup + ETA enrichment (runCode)
      n("2", "runCode", "Dedup and enrich ETA", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse carrier webhook, dedup, and enrich with delivery window
const payload = JSON.parse(vars.webhook_payload || "{}");
const tracking = payload.tracking_number || payload.data?.tracking_number || "";
const carrier = payload.slug || payload.carrier || "unknown";
const status = payload.tag || payload.status || "OutForDelivery";
const checkpoints = payload.checkpoints || payload.data?.checkpoints || [];
const latest = checkpoints.length > 0 ? checkpoints[checkpoints.length - 1] : {};
const estimatedDelivery = payload.expected_delivery || payload.data?.expected_delivery || "";
const customerEmail = payload.customer_email || payload.emails?.[0] || "";
const customerPhone = payload.customer_phone || payload.phones?.[0] || "";
const customerName = payload.customer_name || payload.title || "Customer";
const orderId = payload.order_id || payload.order_number || "";
const pushToken = payload.push_token || payload.device_token || "";
const now = new Date();
const etaDate = estimatedDelivery ? new Date(estimatedDelivery) : null;
const hoursRemaining = etaDate ? Math.max(0, Math.round((etaDate.getTime() - now.getTime()) / 3600000)) : 0;
const result = {
  tracking_number: tracking,
  carrier_slug: carrier,
  delivery_status: status,
  latest_checkpoint: latest.message || latest.description || "Out for delivery",
  latest_location: latest.location || latest.city || "En route",
  customer_email: customerEmail,
  customer_phone: customerPhone,
  customer_name: customerName,
  order_id: orderId,
  push_token: pushToken,
  hours_remaining: hoursRemaining,
  eta_display: hoursRemaining > 0 ? "Arriving in ~" + hoursRemaining + " hours" : "Arriving today",
  has_push_token: pushToken.length > 10 ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "tracking_number", type: "string" },
            { name: "carrier_slug", type: "string" },
            { name: "delivery_status", type: "string" },
            { name: "latest_checkpoint", type: "string" },
            { name: "latest_location", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "push_token", type: "string" },
            { name: "hours_remaining", type: "number" },
            { name: "eta_display", type: "string" },
            { name: "has_push_token", type: "string" },
          ],
        },
      }),

      // 3. Channel preference condition
      n("3", "condition", "Has push notification token?", 520, {
        conditionData: [
          {
            variable: "has_push_token",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Push notification via Firebase (True path)
      pushNode(
        "4",
        "Push: Out-for-delivery alert",
        760,
        "Your order is out for delivery!",
        "{eta_display} — {latest_checkpoint} ({latest_location}). Order #{order_id} via {carrier_slug}. Tracking: {tracking_number}",
      ),

      // 5. Email fallback (False path)
      emailNode(
        "5",
        "Email: Out-for-delivery fallback",
        760,
        "{customer_email}",
        "Your Order #{order_id} Is Out for Delivery!",
        "Hi {customer_name},\\n\\nGreat news! Your package is out for delivery.\\n\\n{eta_display}\\nCarrier: {carrier_slug}\\nTracking: {tracking_number}\\nLatest update: {latest_checkpoint} ({latest_location})\\n\\nKeep an eye out for your delivery today!",
        400,
      ),

      // 6. Failure logging
      sheetsNode(
        "6",
        "Log delivery notification to Sheets",
        1000,
        "DeliveryAlerts",
        '[["{order_id}","{tracking_number}","{carrier_slug}","{delivery_status}","{eta_display}","{latest_location}","{customer_email}","{has_push_token}"]]',
      ),

      // 7. Delivery ops Slack alert
      slackNode(
        "7",
        "Slack: Delivery ops alert",
        1240,
        ":truck: Out-for-Delivery Alert\\nOrder: #{order_id} | Tracking: {tracking_number}\\nCarrier: {carrier_slug} | {eta_display}\\nLocation: {latest_location}\\nCheckpoint: {latest_checkpoint}\\nNotified via: {has_push_token} == yes ? Push : Email",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has push token -> push notification
      e("3", "5", "output_2"), // no push token -> email fallback
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 450. PDP REVISIT BUT NO ADD-TO-CART (8 nodes)
  // ===================================================================

  "PDP Revisit but No Add-to-Cart": {
    summary:
      "Re-engages shoppers who repeatedly view a product page without adding to cart. A storefront event fires after 3+ views, pulls live product data from Shopify, enriches with comparison alternatives, routes on price sensitivity, generates AI objection-handling content, sends a personalized recovery email, delays 24 hours for retargeting, and logs conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Storefront PDP revisit (3+ views)", "webhook_app", {
        source: "Segment",
        event: "product_viewed_3x",
        howItStarts:
          "A customer has viewed the same product detail page 3 or more times without adding to cart, indicating strong interest but hesitation",
        setup:
          "Configure Segment or analytics platform to fire a webhook after 3+ views of the same product by the same visitor within 7 days. Include product ID, visitor/customer email, view count.",
        inputFields: [],
      }),

      // 2. AI extraction of browsing event
      aiNode(
        "2",
        "AI extract browsing intent",
        280,
        "You are a browsing behavior analyst. Parse the storefront event payload to extract the product ID, product handle/slug, customer email, customer name, view count, last viewed timestamp, referrer URL, device type, and any UTM parameters. Determine the hesitation_reason based on view patterns. Respond JSON: {product_id: string, product_handle: string, customer_email: string, customer_name: string, view_count: number, last_viewed: string, referrer: string, device_type: string, utm_source: string, hesitation_reason: string}",
        "{webhook_payload}",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_handle", as: "product_handle", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "view_count", as: "view_count", type: "number" },
          { field: "last_viewed", as: "last_viewed", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          {
            field: "hesitation_reason",
            as: "hesitation_reason",
            type: "string",
          },
        ],
      ),

      // 3. Live product pull from Shopify
      shopifyNode(
        "3",
        "Pull live product details",
        520,
        "GET",
        "products/{product_id}.json?fields=id,title,body_html,variants,images,tags",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.variants[0].compare_at_price",
            customName: "compare_price",
            type: "string",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          { name: "product.tags", customName: "product_tags", type: "string" },
        ],
      ),

      // 4. Comparison enrichment (AI)
      aiNode(
        "4",
        "AI generate comparison context",
        760,
        "You are an e-commerce product comparison expert. Given the product details, generate a brief comparison note highlighting what makes this product stand out vs alternatives, key social proof points, and a value proposition that addresses common hesitations. Respond JSON: {comparison_note: string, social_proof: string, value_proposition: string, price_perception: string}",
        "Product: {product_title} at ${product_price} (compare at ${compare_price}) | Tags: {product_tags} | View count: {view_count} | Hesitation reason: {hesitation_reason}",
        [
          { field: "comparison_note", as: "comparison_note", type: "string" },
          { field: "social_proof", as: "social_proof", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "price_perception", as: "price_perception", type: "string" },
        ],
      ),

      // 5. Price sensitivity condition
      n("5", "condition", "Price-sensitive shopper?", 1000, {
        conditionData: [
          {
            variable: "price_perception",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "hesitation_reason",
            value: "price",
            comparison_operator: "CONTAINS",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI objection-handling content
      aiNode(
        "6",
        "AI craft objection-handling email",
        1240,
        "You are a conversion copywriter specializing in objection handling. Write a personalized recovery email for someone who has viewed a product multiple times without purchasing. Use the comparison context, social proof, and value proposition. For price-sensitive shoppers, emphasize value and payment flexibility. For non-price-sensitive shoppers, emphasize scarcity and social proof. Keep it under 150 words, warm and helpful, not pushy. Respond JSON: {email_subject: string, email_body: string, cta_text: string, urgency_note: string}",
        "Customer: {customer_name} | Product: {product_title} at ${product_price} | Views: {view_count} | Hesitation: {hesitation_reason} | Price perception: {price_perception} | Comparison: {comparison_note} | Social proof: {social_proof} | Value prop: {value_proposition}",
        [
          { field: "email_subject", as: "recovery_subject", type: "string" },
          { field: "email_body", as: "recovery_body", type: "string" },
          { field: "cta_text", as: "recovery_cta", type: "string" },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 7. Recovery email send
      emailNode(
        "7",
        "Send PDP recovery email",
        1480,
        "{customer_email}",
        "{recovery_subject}",
        "{recovery_body}\\n\\n{urgency_note}\\n\\n{recovery_cta}: https://{shopify_store}.myshopify.com/products/{product_handle}",
      ),

      // 8. 24hr retarget delay + conversion logging
      sheetsNode(
        "8",
        "Log PDP recovery to Sheets",
        1720,
        "PDPRecovery",
        '[["{customer_email}","{product_title}","{product_price}","{view_count}","{hesitation_reason}","{price_perception}","{recovery_subject}","{device_type}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // price-sensitive -> objection handling
      e("5", "6", "output_2"), // not price-sensitive -> still send recovery
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 451. PARTIAL SHIPMENT NOTIFICATION (8 nodes)
  // ===================================================================

  "Partial Shipment Notification": {
    summary:
      "Notifies customers when only part of their order ships, with clear details on what shipped and what remains. A partial fulfillment webhook triggers AI extraction, analyzes the order split via runCode to determine shipped vs remaining items, resolves tracking, routes on whether items remain, sends a partial shipment email via Postmark, sends SMS if the customer opted in, alerts fulfillment ops on Slack, and creates a ticket for any inconsistencies.",
    nodes: [
      trigger("Webhook: Shopify partial fulfillment", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A partial fulfillment is created in Shopify, meaning some but not all items in the order have shipped",
        setup:
          "Configure Shopify webhook for fulfillments/create. The workflow detects partial fulfillments by comparing fulfilled items to total order items. Set up Postmark template for partial shipments.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI extract partial fulfillment",
        280,
        "You are a Shopify fulfillment data parser. Extract order ID, fulfillment ID, tracking number, tracking URL, carrier name, fulfilled line items (names and quantities), customer email, customer phone, customer first name, order name, and total line item count from the order. Determine if sms_consent is present. Respond JSON: {order_id: string, fulfillment_id: string, tracking_number: string, tracking_url: string, carrier: string, fulfilled_items: string, fulfilled_count: number, total_order_items: number, customer_email: string, customer_phone: string, customer_name: string, order_name: string, sms_consent: string}",
        "{webhook_payload}",
        [
          { field: "order_id", as: "order_id", type: "string" },
          { field: "fulfillment_id", as: "fulfillment_id", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "tracking_url", as: "tracking_url", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "fulfilled_items", as: "fulfilled_items", type: "string" },
          { field: "fulfilled_count", as: "fulfilled_count", type: "number" },
          {
            field: "total_order_items",
            as: "total_order_items",
            type: "number",
          },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_name", as: "order_name", type: "string" },
          { field: "sms_consent", as: "sms_consent", type: "string" },
        ],
      ),

      // 3. Order split analysis (runCode)
      n("3", "runCode", "Analyze order split", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine what shipped vs what remains in a partial fulfillment
const fulfilledCount = parseInt(vars.fulfilled_count || "0", 10);
const totalItems = parseInt(vars.total_order_items || "0", 10);
const remainingCount = Math.max(0, totalItems - fulfilledCount);
const isPartial = remainingCount > 0;
const fulfillmentPct = totalItems > 0 ? Math.round((fulfilledCount / totalItems) * 100) : 0;
const trackingLink = vars.tracking_url || "https://track.aftership.com/" + (vars.tracking_number || "");
const result = {
  remaining_count: remainingCount,
  is_partial: isPartial ? "yes" : "no",
  fulfillment_percentage: fulfillmentPct,
  shipped_summary: fulfilledCount + " of " + totalItems + " items shipped (" + fulfillmentPct + "%)",
  remaining_summary: remainingCount > 0 ? remainingCount + " items still being prepared" : "All items shipped",
  tracking_link: trackingLink,
  has_sms_consent: (vars.sms_consent || "").toLowerCase() === "yes" ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "remaining_count", type: "number" },
            { name: "is_partial", type: "string" },
            { name: "fulfillment_percentage", type: "number" },
            { name: "shipped_summary", type: "string" },
            { name: "remaining_summary", type: "string" },
            { name: "tracking_link", type: "string" },
            { name: "has_sms_consent", type: "string" },
          ],
        },
      }),

      // 4. Remaining items condition
      n("4", "condition", "Items still remaining?", 760, {
        conditionData: [
          {
            variable: "is_partial",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Partial shipment email via Postmark (True path)
      postmarkNode(
        "5",
        "Postmark: Partial shipment email",
        1000,
        "{customer_email}",
        "partial-shipment",
        `{"name":"{customer_name}","order_number":"{order_name}","shipped_summary":"{shipped_summary}","remaining_summary":"{remaining_summary}","carrier":"{carrier}","tracking_number":"{tracking_number}","tracking_url":"{tracking_link}","fulfilled_items":"{fulfilled_items}"}`,
        [
          { name: "MessageID", customName: "postmark_msg_id", type: "string" },
          { name: "ErrorCode", customName: "postmark_err", type: "number" },
        ],
      ),

      // 6. SMS if consent (Twilio)
      smsNode(
        "6",
        "Twilio: Partial shipment SMS",
        1240,
        "{customer_phone}",
        "Hi {customer_name}! Part of your order {order_name} has shipped: {shipped_summary}. Track: {tracking_link}. {remaining_summary} — we'll notify you when the rest ships!",
      ),

      // 7. Fulfillment Slack alert
      slackNode(
        "7",
        "Slack: Fulfillment ops alert",
        1480,
        ":package: Partial Shipment\\nOrder: {order_name} | Fulfillment: {fulfillment_id}\\nShipped: {shipped_summary}\\nRemaining: {remaining_summary}\\nCarrier: {carrier} | Tracking: {tracking_number}\\nCustomer: {customer_name} ({customer_email})",
      ),

      // 8. Inconsistency ticket
      zendeskNode(
        "8",
        "Zendesk: Fulfillment inconsistency ticket",
        1720,
        "Partial Fulfillment Review - Order {order_name}",
        "Order {order_name} has a partial fulfillment ({shipped_summary}). {remaining_summary}. Carrier: {carrier}, Tracking: {tracking_number}. Review for fulfillment consistency and update customer if delays expected.",
        "normal",
        [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // items remaining -> partial shipment email
      e("4", "5", "output_2"), // all shipped -> still send notification
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 452. PAYMENT RECEIVED CONFIRMATION (7 nodes)
  // ===================================================================

  "Payment Received Confirmation": {
    summary:
      "Confirms successful payments with receipts across channels. A Stripe payment_intent.succeeded webhook triggers invoice and receipt data pull, routes on payment method type, sends a transactional receipt email via Postmark, logs to finance reconciliation Sheets, alerts ops on Slack for failures, and writes an audit record.",
    nodes: [
      trigger("Webhook: Stripe payment_intent.succeeded", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.succeeded",
        howItStarts:
          "A Stripe payment intent succeeds, confirming a customer payment with amount, currency, payment method, and receipt details",
        setup:
          "Configure Stripe webhook for payment_intent.succeeded events. Set up Postmark transactional receipt template. Provide Google Sheets credentials for finance reconciliation.",
        inputFields: [],
      }),

      // 2. Invoice/receipt pull from Stripe
      aiNode(
        "2",
        "AI extract payment details",
        280,
        "You are a Stripe payment data parser. Extract the payment intent ID, amount (in cents), currency, customer ID, customer email, customer name, payment method type (card, bank_transfer, etc.), last four digits of card if applicable, receipt URL, description, and metadata from the webhook payload. Convert amount from cents to dollars. Respond JSON: {payment_intent_id: string, amount_display: string, currency: string, customer_id: string, customer_email: string, customer_name: string, payment_method_type: string, card_last4: string, receipt_url: string, description: string, invoice_id: string}",
        "{webhook_payload}",
        [
          {
            field: "payment_intent_id",
            as: "payment_intent_id",
            type: "string",
          },
          { field: "amount_display", as: "amount_display", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "customer_id", as: "stripe_customer_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "payment_method_type",
            as: "payment_method_type",
            type: "string",
          },
          { field: "card_last4", as: "card_last4", type: "string" },
          { field: "receipt_url", as: "receipt_url", type: "string" },
          { field: "description", as: "payment_description", type: "string" },
          { field: "invoice_id", as: "invoice_id", type: "string" },
        ],
      ),

      // 3. Payment method condition
      n("3", "condition", "Card payment?", 520, {
        conditionData: [
          {
            variable: "payment_method_type",
            value: "card",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Receipt email via Postmark (transactional)
      postmarkNode(
        "4",
        "Postmark: Payment receipt email",
        760,
        "{customer_email}",
        "payment-receipt",
        `{"name":"{customer_name}","amount":"{amount_display}","currency":"{currency}","payment_method":"{payment_method_type}","card_last4":"{card_last4}","receipt_url":"{receipt_url}","description":"{payment_description}","payment_id":"{payment_intent_id}"}`,
        [
          { name: "MessageID", customName: "receipt_msg_id", type: "string" },
          { name: "ErrorCode", customName: "receipt_err", type: "number" },
        ],
      ),

      // 5. Finance reconciliation logging
      sheetsNode(
        "5",
        "Log payment to finance Sheets",
        1000,
        "PaymentReconciliation",
        '[["{payment_intent_id}","{amount_display}","{currency}","{payment_method_type}","{card_last4}","{customer_email}","{stripe_customer_id}","{invoice_id}","{payment_description}"]]',
      ),

      // 6. Failure ops Slack alert
      slackNode(
        "6",
        "Slack: Payment ops alert",
        1240,
        ":white_check_mark: Payment Received\\nPI: {payment_intent_id} | Amount: {amount_display} {currency}\\nMethod: {payment_method_type} (*{card_last4})\\nCustomer: {customer_name} ({customer_email})\\nInvoice: {invoice_id}\\nReceipt: {receipt_url}",
      ),

      // 7. Audit record
      sheetsNode(
        "7",
        "Log payment audit record",
        1480,
        "PaymentAudit",
        '[["{payment_intent_id}","{stripe_customer_id}","{amount_display}","{currency}","{payment_method_type}","{receipt_msg_id}","{receipt_err}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // card payment -> receipt email
      e("3", "4", "output_2"), // non-card -> receipt email too
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 453. PAYMENT-FAILURE RESCUE (10 nodes)
  // ===================================================================

  "Payment-Failure Rescue": {
    summary:
      "Rescues failed payments with intelligent retry sequences based on decline reason. A failed payment webhook triggers decline code translation via runCode, generates a cart restoration link, routes on failure type, sends targeted emails (card update, retry later, or alternate payment), delivers SMS rescue via Twilio, checks recovery after delay, and escalates to finance Slack.",
    nodes: [
      trigger("Webhook: Stripe payment_intent.payment_failed", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.payment_failed",
        howItStarts:
          "A Stripe payment intent fails due to card decline, insufficient funds, expired card, or processing error",
        setup:
          "Configure Stripe webhook for payment_intent.payment_failed. Prepare email templates for each decline type. Set up cart recovery URL generation.",
        inputFields: [],
      }),

      // 2. Decline code translation (runCode)
      n("2", "runCode", "Translate decline code", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Stripe failed payment and translate decline code to actionable category
const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data?.object || payload;
const error = data.last_payment_error || {};
const declineCode = error.decline_code || error.code || "generic_decline";
const amount = data.amount ? (data.amount / 100).toFixed(2) : "0.00";
const currency = (data.currency || "usd").toUpperCase();
const customerEmail = data.receipt_email || data.customer_email || "";
const customerName = data.customer_name || data.metadata?.customer_name || "Customer";
const paymentIntentId = data.id || "";
const cardLast4 = error.payment_method?.card?.last4 || "";
const cardBrand = error.payment_method?.card?.brand || "";
const codeMap = {
  "insufficient_funds": { category: "retry_later", message: "Your payment could not be processed at this time. Please try again shortly.", wait_hours: 24 },
  "card_declined": { category: "update_card", message: "Your card was declined. Please update your payment method.", wait_hours: 0 },
  "expired_card": { category: "update_card", message: "Your card has expired. Please add a new payment method.", wait_hours: 0 },
  "incorrect_cvc": { category: "update_card", message: "The security code was incorrect. Please re-enter your card details.", wait_hours: 0 },
  "processing_error": { category: "retry_later", message: "A temporary processing error occurred. We will retry automatically.", wait_hours: 4 },
  "authentication_required": { category: "alternate_payment", message: "Additional authentication is required. Please try again or use a different payment method.", wait_hours: 0 },
  "generic_decline": { category: "alternate_payment", message: "Your payment could not be completed. Please try a different payment method.", wait_hours: 0 },
};
const mapped = codeMap[declineCode] || codeMap["generic_decline"];
const cartRecoveryUrl = "https://{shopify_store}.myshopify.com/cart/recover?pi=" + paymentIntentId;
const result = {
  payment_intent_id: paymentIntentId,
  decline_code: declineCode,
  failure_category: mapped.category,
  customer_message: mapped.message,
  wait_hours: mapped.wait_hours,
  amount_display: amount,
  currency: currency,
  customer_email: customerEmail,
  customer_name: customerName,
  card_last4: cardLast4,
  card_brand: cardBrand,
  cart_recovery_url: cartRecoveryUrl,
  customer_phone: data.metadata?.phone || ""
};
return result;`,
          outputVariables: [
            { name: "payment_intent_id", type: "string" },
            { name: "decline_code", type: "string" },
            { name: "failure_category", type: "string" },
            { name: "customer_message", type: "string" },
            { name: "wait_hours", type: "number" },
            { name: "amount_display", type: "string" },
            { name: "currency", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "card_last4", type: "string" },
            { name: "card_brand", type: "string" },
            { name: "cart_recovery_url", type: "string" },
            { name: "customer_phone", type: "string" },
          ],
        },
      }),

      // 3. Cart restoration link generation
      n("3", "hitApi", "Generate cart restoration link", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/payment_links",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "line_items[0][price]", value: "{stripe_price_id}" },
            { key: "line_items[0][quantity]", value: "1" },
            { key: "metadata[recovery_pi]", value: "{payment_intent_id}" },
          ],
          variables: [
            { name: "url", customName: "payment_link_url", type: "string" },
            { name: "id", customName: "payment_link_id", type: "string" },
          ],
        },
      }),

      // 4. Failure type condition
      n("4", "condition", "Decline type: update card?", 760, {
        conditionData: [
          {
            variable: "failure_category",
            value: "update_card",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Card update email (True path)
      emailNode(
        "5",
        "Email: Update your payment method",
        1000,
        "{customer_email}",
        "Action Required: Update Your Payment Method",
        "Hi {customer_name},\\n\\n{customer_message}\\n\\nYour {card_brand} card ending in {card_last4} could not process the payment of {amount_display} {currency}.\\n\\nPlease update your payment method to complete your purchase:\\n{payment_link_url}\\n\\nIf you need assistance, our support team is here to help.",
      ),

      // 6. Retry-later email (False path — will also check alternate)
      emailNode(
        "6",
        "Email: We'll retry your payment",
        1000,
        "{customer_email}",
        "Don't Worry — We'll Retry Your Payment",
        "Hi {customer_name},\\n\\n{customer_message}\\n\\nYour payment of {amount_display} {currency} with {card_brand} (*{card_last4}) encountered a temporary issue.\\n\\nWe will automatically retry in {wait_hours} hours. No action is needed on your part.\\n\\nIf you'd prefer to complete the purchase now with a different method:\\n{payment_link_url}",
        400,
      ),

      // 7. Alternate payment email
      emailNode(
        "7",
        "Email: Try a different payment method",
        1000,
        "{customer_email}",
        "Complete Your Purchase with a Different Payment Method",
        "Hi {customer_name},\\n\\n{customer_message}\\n\\nYour payment of {amount_display} {currency} could not be completed. Please try a different payment method:\\n{payment_link_url}\\n\\nWe accept all major cards, Apple Pay, Google Pay, and bank transfers.",
        720,
      ),

      // 8. SMS rescue (Twilio)
      smsNode(
        "8",
        "Twilio: Payment rescue SMS",
        1240,
        "{customer_phone}",
        "Hi {customer_name}, your payment of {amount_display} {currency} needs attention. {customer_message} Complete here: {payment_link_url}",
      ),

      // 9. Recovery delay check
      n("9", "delayAction", "Delay for recovery window", 1480, {
        delayDetails: { delayType: "fixed", days: 1, hours: 0, minutes: 0 },
      }),

      // 10. Finance Slack escalation
      slackNode(
        "10",
        "Slack: Finance payment failure escalation",
        1720,
        ":rotating_light: Payment Failure Rescue\\nPI: {payment_intent_id} | Amount: {amount_display} {currency}\\nDecline: {decline_code} ({failure_category})\\nCard: {card_brand} *{card_last4}\\nCustomer: {customer_name} ({customer_email})\\nRecovery link: {payment_link_url}\\nMessage: {customer_message}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // update_card -> card update email
      e("4", "6", "output_2"), // not update_card -> retry-later
      e("5", "8"), // card update -> SMS
      e("6", "7"), // retry -> alternate payment email
      e("7", "8"), // alternate -> SMS
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 454. PERSONAL SHOPPER BOT (9 nodes)
  // ===================================================================

  "Personal Shopper Bot": {
    summary:
      "Powers an AI personal shopper across chat and WhatsApp channels. A chat or WhatsApp trigger loads CDP context from HubSpot, classifies intent via AI, routes on intent type, performs semantic product search on Shopify, generates a personalized AI response, delivers via the originating channel, checks for escalation to Zendesk, and logs the session to Sheets.",
    nodes: [
      trigger("Webhook: Chat or WhatsApp message", "webhook_app", {
        source: "WhatsApp Business",
        event: "message.received",
        howItStarts:
          "A customer sends a message via chat widget or WhatsApp asking for product recommendations, styling advice, or shopping assistance",
        setup:
          "Configure WhatsApp Business API webhook for incoming messages. Set up HubSpot CDP integration for customer context. Provide Shopify storefront API credentials.",
        inputFields: [],
      }),

      // 2. CDP context load (HubSpot)
      aiNode(
        "2",
        "AI extract message and load context",
        280,
        "You are a message parser for a personal shopper bot. Extract the customer phone or chat ID, message text, customer name if available, message channel (whatsapp or chat), and any product references from the incoming message. Respond JSON: {customer_id: string, customer_phone: string, message_text: string, customer_name: string, channel: string, product_mentions: string, conversation_id: string}",
        "{webhook_payload}",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "channel", as: "message_channel", type: "string" },
          { field: "product_mentions", as: "product_mentions", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
        ],
      ),

      // 3. HubSpot customer context pull
      hubspotNode(
        "3",
        "Load HubSpot customer profile",
        520,
        "GET",
        "crm/v3/objects/contacts/{customer_id}?properties=firstname,lastname,email,lifecycle_stage,hs_buying_role,recent_purchase_category,average_order_value",
        [],
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "properties.lifecycle_stage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.recent_purchase_category",
            customName: "recent_category",
            type: "string",
          },
          {
            name: "properties.average_order_value",
            customName: "avg_order_value",
            type: "string",
          },
        ],
      ),

      // 4. AI intent classification
      aiNode(
        "4",
        "AI classify shopping intent",
        760,
        "You are a shopping intent classifier for a personal shopper bot. Given the customer message and their profile context, classify the intent as one of: product_search, styling_advice, price_inquiry, order_status, complaint, or general_question. Also extract budget range, preferred category, style preferences, and urgency. Respond JSON: {intent: string, budget_range: string, preferred_category: string, style_preferences: string, urgency: string, needs_escalation: string, search_query: string}",
        "Message: {message_text} | Product mentions: {product_mentions} | Customer: {first_name} | Lifecycle: {lifecycle_stage} | Recent category: {recent_category} | Avg order: {avg_order_value}",
        [
          { field: "intent", as: "shopper_intent", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          {
            field: "preferred_category",
            as: "preferred_category",
            type: "string",
          },
          {
            field: "style_preferences",
            as: "style_preferences",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "needs_escalation", as: "needs_escalation", type: "string" },
          { field: "search_query", as: "search_query", type: "string" },
        ],
      ),

      // 5. Intent routing condition
      n("5", "condition", "Product search intent?", 1000, {
        conditionData: [
          {
            variable: "shopper_intent",
            value: "product_search",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "shopper_intent",
            value: "styling_advice",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Semantic product search (Shopify)
      shopifyNode(
        "6",
        "Shopify product search",
        1240,
        "GET",
        "products.json?title={search_query}&limit=5&fields=id,title,variants,images,body_html,tags",
        [],
        [
          { name: "products", customName: "search_results", type: "string" },
          {
            name: "products.length",
            customName: "result_count",
            type: "number",
          },
        ],
      ),

      // 7. AI personalized response
      aiNode(
        "7",
        "AI generate shopper response",
        1480,
        "You are a friendly, knowledgeable personal shopper. Given the customer's intent, profile, and search results, craft a personalized response with product recommendations. Include product names, prices, and brief explanations of why each product suits them. Keep the tone warm and conversational. For WhatsApp, use emojis sparingly. Limit to 3 recommendations max. If no products match, suggest alternatives. Respond JSON: {response_text: string, recommended_products: string, recommendation_count: number, follow_up_question: string}",
        "Intent: {shopper_intent} | Query: {search_query} | Budget: {budget_range} | Category: {preferred_category} | Style: {style_preferences} | Results: {search_results} | Customer: {first_name} | Recent buys: {recent_category}",
        [
          { field: "response_text", as: "bot_response", type: "string" },
          {
            field: "recommended_products",
            as: "recommended_products",
            type: "string",
          },
          {
            field: "recommendation_count",
            as: "recommendation_count",
            type: "number",
          },
          { field: "follow_up_question", as: "follow_up", type: "string" },
        ],
      ),

      // 8. Zendesk escalation condition
      n("8", "condition", "Needs human escalation?", 1720, {
        conditionData: [
          {
            variable: "needs_escalation",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Session logging
      sheetsNode(
        "9",
        "Log shopper session to Sheets",
        1960,
        "PersonalShopperSessions",
        '[["{conversation_id}","{customer_id}","{first_name}","{message_text}","{shopper_intent}","{search_query}","{recommendation_count}","{needs_escalation}","{message_channel}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // product search/styling -> Shopify search
      e("5", "7", "output_2"), // other intent -> AI response directly
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // needs escalation -> log (escalation handled externally)
      e("8", "9", "output_2"), // no escalation -> log
    ],
  },

  // ===================================================================
  // 455. PERSONALIZED RECOMMENDATION DRIP (10 nodes)
  // ===================================================================

  "Personalized Recommendation Drip": {
    summary:
      "Delivers a 3-part personalized recommendation email series after purchase. A purchase trigger pulls CDP and recommendation data, generates AI 3-set recommendations, adjusts segment timing via runCode, sends Day-0 complements email, waits 3 days, sends Day-3 trending email, waits 5 more days, sends Day-8 picked-for-you email, and logs suppression and sequence data.",
    nodes: [
      trigger("Webhook: Order confirmed", "webhook_app", {
        source: "Shopify",
        event: "orders/paid",
        howItStarts:
          "A customer completes a purchase, triggering a personalized 3-email recommendation drip based on their purchase history and browsing behavior",
        setup:
          "Configure Shopify webhook for orders/paid. Set up recommendation engine API or use AI-generated recommendations. Prepare 3 email templates: complements, trending, picked-for-you.",
        inputFields: [],
      }),

      // 2. CDP + recommendation pull (AI extraction)
      aiNode(
        "2",
        "AI extract order and build profile",
        280,
        "You are an e-commerce personalization engine. Parse the order webhook to extract customer email, name, purchased product names, categories, total amount, and order ID. Based on purchased categories, determine the customer segment (fashion, electronics, home, beauty, sports, general). Respond JSON: {customer_email: string, customer_name: string, order_id: string, purchased_items: string, purchased_categories: string, order_total: string, customer_segment: string, purchase_count_estimate: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "purchased_items", as: "purchased_items", type: "string" },
          {
            field: "purchased_categories",
            as: "purchased_categories",
            type: "string",
          },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "customer_segment", as: "customer_segment", type: "string" },
          {
            field: "purchase_count_estimate",
            as: "purchase_count_est",
            type: "string",
          },
        ],
      ),

      // 3. AI 3-set recommendation generation
      aiNode(
        "3",
        "AI generate 3-set recommendations",
        520,
        "You are a product recommendation engine. Given the customer's purchased items, categories, and segment, generate three sets of recommendations: (1) complementary products that go with their purchase, (2) trending products in their segment, (3) personally picked items based on their profile. Each set should have 3 products with name, price range, and one-line reason. Respond JSON: {complements_subject: string, complements_body: string, trending_subject: string, trending_body: string, picked_subject: string, picked_body: string, total_recommendations: number}",
        "Purchased: {purchased_items} | Categories: {purchased_categories} | Segment: {customer_segment} | Order total: {order_total} | Purchase history est: {purchase_count_est}",
        [
          {
            field: "complements_subject",
            as: "complements_subject",
            type: "string",
          },
          { field: "complements_body", as: "complements_body", type: "string" },
          { field: "trending_subject", as: "trending_subject", type: "string" },
          { field: "trending_body", as: "trending_body", type: "string" },
          { field: "picked_subject", as: "picked_subject", type: "string" },
          { field: "picked_body", as: "picked_body", type: "string" },
          { field: "total_recommendations", as: "total_recs", type: "number" },
        ],
      ),

      // 4. Segment timing adjustment (runCode)
      n("4", "runCode", "Adjust drip timing by segment", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Adjust email drip timing based on customer segment and purchase behavior
const segment = vars.customer_segment || "general";
const purchaseEst = parseInt(vars.purchase_count_est || "1", 10);
const isRepeat = purchaseEst > 1;
const timingMap = {
  "fashion": { day1_delay: 0, day2_delay: 3, day3_delay: 5 },
  "electronics": { day1_delay: 0, day2_delay: 5, day3_delay: 7 },
  "home": { day1_delay: 0, day2_delay: 4, day3_delay: 6 },
  "beauty": { day1_delay: 0, day2_delay: 3, day3_delay: 5 },
  "sports": { day1_delay: 0, day2_delay: 4, day3_delay: 6 },
  "general": { day1_delay: 0, day2_delay: 3, day3_delay: 5 },
};
const timing = timingMap[segment] || timingMap["general"];
const result = {
  drip1_delay_days: timing.day1_delay,
  drip2_delay_days: timing.day2_delay,
  drip3_delay_days: timing.day3_delay,
  is_repeat_buyer: isRepeat ? "yes" : "no",
  segment_label: segment.charAt(0).toUpperCase() + segment.slice(1),
  suppression_key: vars.customer_email + "_rec_drip_" + vars.order_id
};
return result;`,
          outputVariables: [
            { name: "drip1_delay_days", type: "number" },
            { name: "drip2_delay_days", type: "number" },
            { name: "drip3_delay_days", type: "number" },
            { name: "is_repeat_buyer", type: "string" },
            { name: "segment_label", type: "string" },
            { name: "suppression_key", type: "string" },
          ],
        },
      }),

      // 5. Day-0 complements email
      emailNode(
        "5",
        "Email: Day-0 complementary picks",
        1000,
        "{customer_email}",
        "{complements_subject}",
        "Hi {customer_name},\\n\\nThank you for your recent order! Based on what you purchased, we think you'll love these complementary picks:\\n\\n{complements_body}\\n\\nHappy shopping!",
      ),

      // 6. 3-day delay
      n("6", "delayAction", "Delay 3 days for trending email", 1240, {
        delayDetails: { delayType: "fixed", days: 3, hours: 0, minutes: 0 },
      }),

      // 7. Day-3 trending email
      emailNode(
        "7",
        "Email: Day-3 trending in your segment",
        1480,
        "{customer_email}",
        "{trending_subject}",
        "Hi {customer_name},\\n\\nHere's what's trending in {segment_label} right now:\\n\\n{trending_body}\\n\\nDon't miss out on these popular picks!",
      ),

      // 8. 5-day delay
      n("8", "delayAction", "Delay 5 days for picked-for-you email", 1720, {
        delayDetails: { delayType: "fixed", days: 5, hours: 0, minutes: 0 },
      }),

      // 9. Day-8 picked-for-you email
      emailNode(
        "9",
        "Email: Day-8 picked just for you",
        1960,
        "{customer_email}",
        "{picked_subject}",
        "Hi {customer_name},\\n\\nWe handpicked these just for you based on your style and preferences:\\n\\n{picked_body}\\n\\nWe hope you love them as much as we do!",
      ),

      // 10. Sequence suppression + logging
      sheetsNode(
        "10",
        "Log recommendation drip to Sheets",
        2200,
        "RecDripLog",
        '[["{customer_email}","{order_id}","{customer_segment}","{purchased_categories}","{total_recs}","{is_repeat_buyer}","{suppression_key}","{complements_subject}","{trending_subject}","{picked_subject}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 456. PINTEREST CATALOG FEED SYNC (8 nodes)
  // ===================================================================

  "Pinterest Catalog Feed Sync": {
    summary:
      "Syncs product catalog to Pinterest for shopping pins via scheduled feed upload. A scheduled sync exports the Shopify catalog, transforms it to Pinterest feed format via runCode, validates required fields, uploads the feed, polls processing status, summarizes on Slack, and logs errors to Sheets.",
    nodes: [
      trigger("Schedule: Pinterest catalog sync", "schedule", {
        schedule: "0 3 * * * (daily at 3 AM UTC)",
        howItStarts:
          "A daily scheduled job exports the full product catalog from Shopify and syncs it to Pinterest for shopping pins and product discovery",
        setup:
          "Configure Shopify API credentials. Set up Pinterest Business API with catalog access. Create a Pinterest catalog and note the feed ID.",
        inputFields: [],
      }),

      // 2. Shopify catalog export
      shopifyNode(
        "2",
        "Export Shopify product catalog",
        280,
        "GET",
        "products.json?status=active&limit=250&fields=id,title,body_html,vendor,product_type,tags,variants,images",
        [],
        [
          { name: "products", customName: "catalog_products", type: "string" },
          {
            name: "products.length",
            customName: "product_count",
            type: "number",
          },
        ],
      ),

      // 3. Pinterest feed transform (runCode)
      n("3", "runCode", "Transform to Pinterest feed format", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Transform Shopify products to Pinterest catalog feed format
const products = JSON.parse(vars.catalog_products || "[]");
let validCount = 0;
let invalidCount = 0;
const errors = [];
const feedItems = products.map(p => {
  const variant = (p.variants || [])[0] || {};
  const image = (p.images || [])[0] || {};
  const price = variant.price ? parseFloat(variant.price) : 0;
  const hasRequired = p.title && price > 0 && image.src;
  if (!hasRequired) {
    invalidCount++;
    errors.push(p.id + ": missing " + (!p.title ? "title" : !price ? "price" : "image"));
    return null;
  }
  validCount++;
  return {
    id: String(variant.sku || variant.id || p.id),
    title: p.title,
    description: (p.body_html || "").replace(/<[^>]*>/g, "").substring(0, 500),
    link: "https://{shopify_store}.myshopify.com/products/" + (p.handle || p.id),
    image_link: image.src,
    price: price.toFixed(2) + " USD",
    availability: (variant.inventory_quantity || 0) > 0 ? "in stock" : "out of stock",
    brand: p.vendor || "{store_name}",
    google_product_category: p.product_type || "All Products",
    condition: "new"
  };
}).filter(Boolean);
const result = {
  feed_items: JSON.stringify(feedItems),
  valid_count: validCount,
  invalid_count: invalidCount,
  error_details: errors.join("; ").substring(0, 500),
  has_errors: invalidCount > 0 ? "yes" : "no",
  feed_size: feedItems.length
};
return result;`,
          outputVariables: [
            { name: "feed_items", type: "string" },
            { name: "valid_count", type: "number" },
            { name: "invalid_count", type: "number" },
            { name: "error_details", type: "string" },
            { name: "has_errors", type: "string" },
            { name: "feed_size", type: "number" },
          ],
        },
      }),

      // 4. Field validation condition
      n("4", "condition", "All items valid?", 760, {
        conditionData: [
          {
            variable: "has_errors",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Feed upload to Pinterest
      n("5", "hitApi", "Upload feed to Pinterest", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/catalogs/feeds/{pinterest_feed_id}/items/batch",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "operation", value: "UPSERT" },
            { key: "items", value: "{feed_items}" },
          ],
          variables: [
            {
              name: "batch_id",
              customName: "pinterest_batch_id",
              type: "string",
            },
            { name: "status", customName: "upload_status", type: "string" },
          ],
        },
      }),

      // 6. Processing status poll
      n("6", "hitApi", "Poll Pinterest processing status", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.pinterest.com/v5/catalogs/feeds/{pinterest_feed_id}/processing_results?batch_id={pinterest_batch_id}",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
          ],
          body: [],
          variables: [
            { name: "status", customName: "processing_status", type: "string" },
            {
              name: "items_total",
              customName: "items_processed",
              type: "number",
            },
            {
              name: "items_succeeded",
              customName: "items_succeeded",
              type: "number",
            },
            {
              name: "items_failed",
              customName: "items_failed",
              type: "number",
            },
          ],
        },
      }),

      // 7. Slack summary
      slackNode(
        "7",
        "Slack: Pinterest sync summary",
        1480,
        ":pushpin: Pinterest Catalog Sync Complete\\nProducts exported: {product_count}\\nValid items: {valid_count} | Invalid: {invalid_count}\\nProcessed: {items_processed} | Succeeded: {items_succeeded} | Failed: {items_failed}\\nBatch: {pinterest_batch_id} | Status: {processing_status}\\nErrors: {error_details}",
      ),

      // 8. Error logging
      sheetsNode(
        "8",
        "Log sync results to Sheets",
        1720,
        "PinterestCatalogSync",
        '[["{product_count}","{valid_count}","{invalid_count}","{items_processed}","{items_succeeded}","{items_failed}","{processing_status}","{pinterest_batch_id}","{error_details}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // all valid -> upload
      e("4", "5", "output_2"), // some invalid -> upload anyway (partial)
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 457. PINTEREST PRODUCT PIN SYNC (7 nodes)
  // ===================================================================

  "Pinterest Product Pin Sync": {
    summary:
      "Syncs changed products to Pinterest as rich pins with UTM tracking. A batch of changed products triggers UTM parameter appending via runCode, generates AI pin metadata (titles, descriptions, alt text), batch upserts to Pinterest, routes on failed items, sends Slack sync report, and logs pin audit data to Sheets.",
    nodes: [
      trigger("Webhook: Shopify product batch update", "webhook_app", {
        source: "Shopify",
        event: "products/update",
        howItStarts:
          "One or more products are updated in Shopify (price, title, description, image changes), triggering a sync of rich pin data to Pinterest",
        setup:
          "Configure Shopify webhook for products/update. Set up Pinterest Business API credentials with pin creation scope. Define UTM parameters for Pinterest attribution.",
        inputFields: [],
      }),

      // 2. UTM append (runCode)
      n("2", "runCode", "Append UTM parameters to product URLs", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse changed products and append Pinterest UTM tracking
const payload = JSON.parse(vars.webhook_payload || "{}");
const products = Array.isArray(payload) ? payload : [payload];
const utmSource = "pinterest";
const utmMedium = "organic_pin";
const utmCampaign = "product_sync";
const enriched = products.map(p => {
  const handle = p.handle || p.id;
  const baseUrl = "https://{shopify_store}.myshopify.com/products/" + handle;
  const utmUrl = baseUrl + "?utm_source=" + utmSource + "&utm_medium=" + utmMedium + "&utm_campaign=" + utmCampaign + "&utm_content=" + handle;
  return {
    product_id: String(p.id || ""),
    title: p.title || "",
    description: (p.body_html || "").replace(/<[^>]*>/g, "").substring(0, 500),
    image_url: (p.images || [])[0]?.src || "",
    price: (p.variants || [])[0]?.price || "0.00",
    link: utmUrl,
    handle: handle
  };
});
const result = {
  enriched_products: JSON.stringify(enriched),
  product_count: enriched.length,
  first_product_title: enriched[0] ? enriched[0].title : "",
  first_product_id: enriched[0] ? enriched[0].product_id : "",
  utm_pattern: utmSource + "/" + utmMedium + "/" + utmCampaign
};
return result;`,
          outputVariables: [
            { name: "enriched_products", type: "string" },
            { name: "product_count", type: "number" },
            { name: "first_product_title", type: "string" },
            { name: "first_product_id", type: "string" },
            { name: "utm_pattern", type: "string" },
          ],
        },
      }),

      // 3. AI pin metadata generation
      aiNode(
        "3",
        "AI generate pin metadata",
        520,
        "You are a Pinterest marketing specialist. Given product data with UTM-tracked URLs, generate optimized pin metadata for each product: a Pinterest-optimized title (under 100 chars, keyword-rich), a compelling pin description (under 500 chars with relevant hashtags), and alt text for accessibility. Respond JSON: {pin_items: string, pin_count: number, hashtags_used: string, optimization_notes: string}",
        "Products: {enriched_products} | Count: {product_count}",
        [
          { field: "pin_items", as: "pin_items", type: "string" },
          { field: "pin_count", as: "pin_count", type: "number" },
          { field: "hashtags_used", as: "hashtags_used", type: "string" },
          {
            field: "optimization_notes",
            as: "pin_optimization",
            type: "string",
          },
        ],
      ),

      // 4. Batch upsert to Pinterest
      n("4", "hitApi", "Batch upsert pins to Pinterest", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/pins/batch",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "items", value: "{pin_items}" },
            { key: "board_id", value: "{pinterest_board_id}" },
          ],
          variables: [
            {
              name: "items_succeeded",
              customName: "pins_created",
              type: "number",
            },
            { name: "items_failed", customName: "pins_failed", type: "number" },
            { name: "batch_id", customName: "pin_batch_id", type: "string" },
          ],
        },
      }),

      // 5. Failed items condition
      n("5", "condition", "Any pins failed?", 1000, {
        conditionData: [
          {
            variable: "pins_failed",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Slack sync report
      slackNode(
        "6",
        "Slack: Pinterest pin sync report",
        1240,
        ":art: Pinterest Pin Sync Report\\nProducts synced: {product_count}\\nPins created: {pins_created} | Failed: {pins_failed}\\nBatch: {pin_batch_id}\\nUTM: {utm_pattern}\\nHashtags: {hashtags_used}\\nNotes: {pin_optimization}",
      ),

      // 7. Pin audit logging
      sheetsNode(
        "7",
        "Log pin audit to Sheets",
        1480,
        "PinterestPinAudit",
        '[["{first_product_id}","{first_product_title}","{product_count}","{pins_created}","{pins_failed}","{pin_batch_id}","{utm_pattern}","{hashtags_used}","{pin_optimization}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // failures -> Slack (with alert)
      e("5", "6", "output_2"), // no failures -> Slack (success)
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 458. POST-PURCHASE SHIPPING EDUCATION (7 nodes)
  // ===================================================================

  "Post-Purchase Shipping Education": {
    summary:
      "Educates first-time buyers about shipping expectations based on order complexity. A first-buyer order trigger detects shipping method and complexity via runCode, routes on complexity level, sends international customs education email or simple domestic confirmation, logs engagement to the CDP, and alerts ops on failures.",
    nodes: [
      trigger("Webhook: First-time buyer order", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A first-time buyer places an order, triggering shipping education content tailored to their order's shipping method, destination, and complexity",
        setup:
          "Configure Shopify webhook for orders/create. Filter for first-time buyers (orders_count == 1). Prepare email templates for international and domestic shipping education.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI extract first-buyer order details",
        280,
        "You are an order data parser. Extract the customer email, customer first name, order ID, order name, shipping country, shipping method, shipping address (full), line item count, contains_fragile flag (check product tags), contains_oversized flag, estimated delivery date, and total weight if available. Determine if this is an international order. Respond JSON: {customer_email: string, customer_name: string, order_id: string, order_name: string, ship_country: string, ship_method: string, ship_address: string, item_count: number, is_fragile: string, is_oversized: string, est_delivery: string, is_international: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_name", as: "order_name", type: "string" },
          { field: "ship_country", as: "ship_country", type: "string" },
          { field: "ship_method", as: "ship_method", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "is_fragile", as: "is_fragile", type: "string" },
          { field: "is_oversized", as: "is_oversized", type: "string" },
          { field: "est_delivery", as: "est_delivery", type: "string" },
          { field: "is_international", as: "is_international", type: "string" },
        ],
      ),

      // 3. Shipping complexity detection (runCode)
      n("3", "runCode", "Detect shipping complexity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify shipping complexity based on destination, method, and item attributes
const isIntl = (vars.is_international || "").toLowerCase() === "yes";
const isFrag = (vars.is_fragile || "").toLowerCase() === "yes";
const isOver = (vars.is_oversized || "").toLowerCase() === "yes";
const itemCount = parseInt(vars.item_count || "1", 10);
let complexity = "simple";
const reasons = [];
if (isIntl) { complexity = "complex"; reasons.push("international shipping"); }
if (isFrag) { complexity = "complex"; reasons.push("fragile items"); }
if (isOver) { complexity = "complex"; reasons.push("oversized items"); }
if (itemCount > 5) { reasons.push("multi-item order"); if (complexity === "simple") complexity = "moderate"; }
const shipMethod = vars.ship_method || "standard";
if (shipMethod.toLowerCase().includes("freight") || shipMethod.toLowerCase().includes("ltl")) {
  complexity = "complex";
  reasons.push("freight shipping");
}
const result = {
  shipping_complexity: complexity,
  complexity_reasons: reasons.length > 0 ? reasons.join(", ") : "standard domestic shipping",
  needs_customs_info: isIntl ? "yes" : "no",
  delivery_estimate: vars.est_delivery || "5-7 business days",
  education_type: complexity === "complex" ? "international_customs" : "simple_domestic"
};
return result;`,
          outputVariables: [
            { name: "shipping_complexity", type: "string" },
            { name: "complexity_reasons", type: "string" },
            { name: "needs_customs_info", type: "string" },
            { name: "delivery_estimate", type: "string" },
            { name: "education_type", type: "string" },
          ],
        },
      }),

      // 4. Complexity condition
      n("4", "condition", "Complex shipping?", 760, {
        conditionData: [
          {
            variable: "shipping_complexity",
            value: "complex",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. International customs email (True path)
      emailNode(
        "5",
        "Email: International shipping guide",
        1000,
        "{customer_email}",
        "Your Order {order_name} — International Shipping Guide",
        "Hi {customer_name},\\n\\nThank you for your order! Since your package is shipping internationally to {ship_country}, here's what to expect:\\n\\n1. Customs Processing: International orders may go through customs inspection, which can add 2-5 business days.\\n2. Duties & Taxes: Depending on your country, you may be responsible for import duties. These are not included in your order total.\\n3. Tracking: Your tracking may pause during customs. This is normal.\\n4. Special Handling: {complexity_reasons}\\n\\nEstimated delivery: {delivery_estimate}\\n\\nQuestions? Reply to this email and our team will help!",
      ),

      // 6. Simple domestic email (False path)
      emailNode(
        "6",
        "Email: Domestic shipping confirmation",
        1000,
        "{customer_email}",
        "Your Order {order_name} Is Being Prepared!",
        "Hi {customer_name},\\n\\nThank you for your first order with us! Here's what happens next:\\n\\n1. We're preparing your {item_count} item(s) for shipping via {ship_method}.\\n2. You'll receive a tracking email once your package ships.\\n3. Estimated delivery: {delivery_estimate}\\n\\nWelcome to the family!",
        400,
      ),

      // 7. CDP engagement logging + failure alert
      sheetsNode(
        "7",
        "Log shipping education engagement",
        1240,
        "ShippingEducation",
        '[["{customer_email}","{order_name}","{ship_country}","{shipping_complexity}","{complexity_reasons}","{education_type}","{delivery_estimate}","{is_international}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // complex -> international guide
      e("4", "6", "output_2"), // simple -> domestic confirmation
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 459. POST-PURCHASE BY CATEGORY SEGMENT (8 nodes)
  // ===================================================================

  "Post-Purchase by Category Segment": {
    summary:
      "Sends category-specific post-purchase content based on the dominant product category. A fulfillment trigger computes the dominant category via runCode, routes on category type, sends a targeted setup guide email for electronics, styling ideas for fashion, care instructions for home goods, or a generic fallback, and alerts on content gaps via Slack.",
    nodes: [
      trigger("Webhook: Shopify order fulfilled", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "An order is fulfilled in Shopify, triggering category-specific post-purchase content based on what the customer bought",
        setup:
          "Configure Shopify webhook for fulfillments/create. Prepare category-specific email templates for electronics (setup guide), fashion (styling), home (care), and generic fallback.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI extract order categories",
        280,
        "You are an order categorization specialist. Parse the fulfillment webhook to extract customer email, customer name, order name, and all line items with their product types and tags. List all product categories found. Respond JSON: {customer_email: string, customer_name: string, order_name: string, line_items: string, product_categories: string, item_count: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_name", as: "order_name", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
          { field: "item_count", as: "item_count", type: "number" },
        ],
      ),

      // 3. Dominant category computation (runCode)
      n("3", "runCode", "Compute dominant category", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine the dominant product category from the order
const categories = (vars.product_categories || "").toLowerCase();
const categoryKeywords = {
  "electronics": ["electronics", "tech", "gadget", "computer", "phone", "tablet", "camera", "audio", "smart"],
  "fashion": ["fashion", "clothing", "apparel", "shoes", "accessories", "jewelry", "dress", "shirt", "pants"],
  "home": ["home", "furniture", "kitchen", "bath", "bedding", "decor", "garden", "cleaning", "storage"],
  "beauty": ["beauty", "skincare", "makeup", "cosmetics", "hair", "fragrance", "wellness"],
  "sports": ["sports", "fitness", "outdoor", "camping", "cycling", "running", "yoga"]
};
let dominant = "general";
let maxScore = 0;
for (const [cat, keywords] of Object.entries(categoryKeywords)) {
  const score = keywords.filter(kw => categories.includes(kw)).length;
  if (score > maxScore) { maxScore = score; dominant = cat; }
}
const contentMap = {
  "electronics": "setup_guide",
  "fashion": "styling_ideas",
  "home": "care_instructions",
  "beauty": "care_instructions",
  "sports": "setup_guide",
  "general": "generic_fallback"
};
const result = {
  dominant_category: dominant,
  content_type: contentMap[dominant] || "generic_fallback",
  category_confidence: maxScore > 0 ? "matched" : "no_match",
  all_categories: vars.product_categories || "uncategorized"
};
return result;`,
          outputVariables: [
            { name: "dominant_category", type: "string" },
            { name: "content_type", type: "string" },
            { name: "category_confidence", type: "string" },
            { name: "all_categories", type: "string" },
          ],
        },
      }),

      // 4. Category routing condition
      n("4", "condition", "Electronics category?", 760, {
        conditionData: [
          {
            variable: "content_type",
            value: "setup_guide",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Setup guide email (True path - electronics/sports)
      emailNode(
        "5",
        "Email: Product setup guide",
        1000,
        "{customer_email}",
        "Get the Most Out of Your {order_name} Purchase — Setup Guide",
        "Hi {customer_name},\\n\\nYour order {order_name} has been delivered! Here's a quick setup guide to get you started:\\n\\n1. Unbox carefully and check all components\\n2. Download any companion apps referenced in the packaging\\n3. Follow the quick-start guide included\\n4. Register your product for warranty coverage\\n\\nItems: {line_items}\\n\\nNeed help? Our tech support team is just a reply away!",
      ),

      // 6. Styling ideas email (False path — fashion check)
      emailNode(
        "6",
        "Email: Styling ideas for your new looks",
        1000,
        "{customer_email}",
        "Style Inspo for Your New Pieces — {order_name}",
        "Hi {customer_name},\\n\\nYour fashion order {order_name} has arrived! Here are some styling ideas:\\n\\n- Mix and match with neutrals for everyday wear\\n- Layer pieces for a polished look\\n- Accessorize to elevate any outfit\\n\\nItems: {line_items}\\n\\nTag us on social media — we'd love to see your style!",
        400,
      ),

      // 7. Care instructions email (home/beauty)
      emailNode(
        "7",
        "Email: Care instructions for your order",
        1000,
        "{customer_email}",
        "Care Tips for Your {order_name} Purchase",
        "Hi {customer_name},\\n\\nYour order {order_name} is here! To keep your items looking their best:\\n\\n- Follow the care labels on each product\\n- Store in a cool, dry place\\n- Clean with recommended products only\\n\\nItems: {line_items}\\n\\nReach out if you have any care questions!",
        720,
      ),

      // 8. Content gap Slack alert
      slackNode(
        "8",
        "Slack: Content gap alert",
        1240,
        ":memo: Post-Purchase Content Dispatch\\nOrder: {order_name} | Customer: {customer_name}\\nDominant category: {dominant_category} | Content type: {content_type}\\nConfidence: {category_confidence}\\nAll categories: {all_categories}\\nItems: {item_count}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // setup_guide -> electronics/sports guide
      e("4", "6", "output_2"), // not setup_guide -> styling (fashion path)
      e("5", "8"),
      e("6", "8"), // fashion path also alerts
      e("7", "8"), // care path also alerts
    ],
  },

  // ===================================================================
  // 460. POST-PURCHASE BY ORDER-COUNT SEGMENT (7 nodes)
  // ===================================================================

  "Post-Purchase by Order-Count Segment": {
    summary:
      "Segments post-purchase messaging based on customer order history. An order trigger pulls customer order history from Shopify, classifies the customer into first-time, repeat, or VIP segments via condition, sends a tailored onboarding email for first-timers, appreciation email for repeats, or white-glove VIP email, and logs the segment to HubSpot.",
    nodes: [
      trigger("Webhook: New order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A customer places an order, triggering segmented post-purchase messaging based on their total order count and lifetime value",
        setup:
          "Configure Shopify webhook for orders/create. Set up HubSpot for segment logging. Prepare three email templates: first-time brand onboarding, repeat appreciation, and VIP white-glove.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI extract order and customer data",
        280,
        "You are an order data parser. Extract customer email, customer first name, order name, order total, customer ID, and the customer's orders_count from the webhook payload. Respond JSON: {customer_email: string, customer_name: string, order_name: string, order_total: string, customer_id: string, orders_count: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_name", as: "order_name", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "orders_count", as: "orders_count", type: "number" },
        ],
      ),

      // 3. Segment classification condition
      n("3", "condition", "First-time buyer?", 520, {
        conditionData: [
          {
            variable: "orders_count",
            value: "1",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. First-time brand onboarding email (True path)
      emailNode(
        "4",
        "Email: First-time buyer onboarding",
        760,
        "{customer_email}",
        "Welcome to the Family, {customer_name}!",
        "Hi {customer_name},\\n\\nThank you for your first order ({order_name})! We're thrilled to have you.\\n\\nHere's what makes us special:\\n- Quality guarantee on every product\\n- Free returns within 30 days\\n- Loyalty rewards starting with your next purchase\\n\\nYour order of {order_total} is being prepared now. We'll send tracking details shortly.\\n\\nWelcome aboard!",
      ),

      // 5. Repeat appreciation email (False path — will check VIP next)
      emailNode(
        "5",
        "Email: Repeat buyer appreciation",
        760,
        "{customer_email}",
        "Thanks for Coming Back, {customer_name}!",
        "Hi {customer_name},\\n\\nOrder #{orders_count} — you're on a roll! Thank you for your continued trust in us.\\n\\nYour order {order_name} ({order_total}) is being prepared with care.\\n\\nAs a valued repeat customer, enjoy exclusive early access to our upcoming sales. Keep an eye on your inbox!\\n\\nThank you for being part of our community.",
        400,
      ),

      // 6. VIP white-glove email
      emailNode(
        "6",
        "Email: VIP white-glove experience",
        760,
        "{customer_email}",
        "VIP Treatment for Our Top Customer, {customer_name}",
        "Hi {customer_name},\\n\\nAs one of our most valued customers (order #{orders_count}!), you deserve the best.\\n\\nYour order {order_name} ({order_total}) has been flagged for priority processing and will ship first.\\n\\nYour VIP benefits:\\n- Priority shipping on every order\\n- Dedicated support line\\n- Exclusive VIP-only collections\\n- Birthday surprises\\n\\nThank you for your incredible loyalty!",
        720,
      ),

      // 7. Segment logging (HubSpot)
      hubspotNode(
        "7",
        "Log segment to HubSpot",
        1000,
        "PATCH",
        "crm/v3/objects/contacts/{customer_id}",
        [
          {
            key: "properties",
            value:
              '{"order_segment":"{orders_count}","total_orders":"{orders_count}","last_order":"{order_name}","last_order_value":"{order_total}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // first-time -> onboarding
      e("3", "5", "output_2"), // repeat -> appreciation
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 461. PRICE CHANGE MULTI-PLATFORM PROPAGATION (10 nodes)
  // ===================================================================

  "Price Change Multi-Platform Propagation": {
    summary:
      "Propagates price changes from PIM across Google Merchant, Meta, Amazon, and eBay with MAP validation and approval. A PIM price change trigger validates pricing and checks MAP compliance via runCode, routes on approval, pushes to Google Merchant Center, Meta catalog, Amazon SP-API, and eBay Trading API, aggregates platform results, summarizes on Slack, and logs the full propagation audit to Sheets.",
    nodes: [
      trigger("Webhook: PIM price change event", "webhook_app", {
        source: "PIM System",
        event: "product.price.updated",
        howItStarts:
          "A product price is changed in the PIM system, triggering validation and propagation to all connected sales channels",
        setup:
          "Configure PIM webhook for price update events. Set up API credentials for Google Merchant Center, Meta Commerce, Amazon SP-API, and eBay Trading API. Define MAP (Minimum Advertised Price) rules.",
        inputFields: [],
      }),

      // 2. Validation + MAP check (runCode)
      n("2", "runCode", "Validate price and check MAP compliance", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate pricing data and check MAP (Minimum Advertised Price) compliance
const payload = JSON.parse(vars.webhook_payload || "{}");
const productId = payload.product_id || payload.sku || "";
const productTitle = payload.title || payload.name || "";
const newPrice = parseFloat(payload.new_price || payload.price || "0");
const oldPrice = parseFloat(payload.old_price || payload.previous_price || "0");
const currency = payload.currency || "USD";
const mapPrice = parseFloat(payload.map_price || payload.minimum_price || "0");
const msrp = parseFloat(payload.msrp || payload.list_price || "0");
const priceValid = newPrice > 0 && newPrice < 100000;
const mapCompliant = mapPrice <= 0 || newPrice >= mapPrice;
const priceDirection = newPrice > oldPrice ? "increase" : newPrice < oldPrice ? "decrease" : "unchanged";
const changePct = oldPrice > 0 ? Math.abs(((newPrice - oldPrice) / oldPrice) * 100).toFixed(1) : "0";
const needsApproval = !mapCompliant || parseFloat(changePct) > 25;
const result = {
  product_id: productId,
  product_title: productTitle,
  new_price: newPrice.toFixed(2),
  old_price: oldPrice.toFixed(2),
  currency: currency,
  map_price: mapPrice.toFixed(2),
  msrp: msrp.toFixed(2),
  price_valid: priceValid ? "yes" : "no",
  map_compliant: mapCompliant ? "yes" : "no",
  price_direction: priceDirection,
  change_percentage: changePct,
  approved: (!needsApproval && priceValid) ? "yes" : "no",
  rejection_reason: !priceValid ? "Invalid price" : !mapCompliant ? "Below MAP ($" + mapPrice.toFixed(2) + ")" : parseFloat(changePct) > 25 ? "Large change (" + changePct + "%) needs review" : ""
};
return result;`,
          outputVariables: [
            { name: "product_id", type: "string" },
            { name: "product_title", type: "string" },
            { name: "new_price", type: "string" },
            { name: "old_price", type: "string" },
            { name: "currency", type: "string" },
            { name: "map_price", type: "string" },
            { name: "msrp", type: "string" },
            { name: "price_valid", type: "string" },
            { name: "map_compliant", type: "string" },
            { name: "price_direction", type: "string" },
            { name: "change_percentage", type: "string" },
            { name: "approved", type: "string" },
            { name: "rejection_reason", type: "string" },
          ],
        },
      }),

      // 3. Approval condition
      n("3", "condition", "Price change approved?", 520, {
        conditionData: [
          {
            variable: "approved",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Google Merchant Center push
      n("4", "hitApi", "Push to Google Merchant Center", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products",
          headers: [
            { key: "Authorization", value: "Bearer {google_merchant_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "offerId", value: "{product_id}" },
            {
              key: "price",
              value: `{"value":"{new_price}","currency":"{currency}"}`,
            },
          ],
          variables: [
            { name: "id", customName: "gmc_product_id", type: "string" },
            { name: "kind", customName: "gmc_status", type: "string" },
          ],
        },
      }),

      // 5. Meta catalog push
      n("5", "hitApi", "Push to Meta product catalog", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/items_batch",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "requests",
              value: `[{"method":"UPDATE","retailer_id":"{product_id}","data":{"price":"{new_price} {currency}"}}]`,
            },
          ],
          variables: [
            {
              name: "handles",
              customName: "meta_batch_handle",
              type: "string",
            },
          ],
        },
      }),

      // 6. Amazon SP-API push
      n("6", "hitApi", "Push to Amazon SP-API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sellingpartnerapi-na.amazon.com/listings/2021-08-01/items/{amazon_seller_id}/{product_id}",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_sp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "productType", value: "PRODUCT" },
            {
              key: "patches",
              value: `[{"op":"replace","path":"/attributes/purchasable_offer","value":[{"currency":"{currency}","our_price":[{"schedule":[{"value_with_tax":"{new_price}"}]}]}]}]`,
            },
          ],
          variables: [
            { name: "status", customName: "amazon_status", type: "string" },
            {
              name: "submissionId",
              customName: "amazon_submission_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. eBay revision
      n("7", "hitApi", "Push to eBay Trading API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ebay.com/sell/inventory/v1/offer/{ebay_offer_id}",
          headers: [
            { key: "Authorization", value: "Bearer {ebay_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "pricingSummary",
              value: `{"price":{"value":"{new_price}","currency":"{currency}"}}`,
            },
          ],
          variables: [
            {
              name: "offerId",
              customName: "ebay_offer_id_result",
              type: "string",
            },
            { name: "statusCode", customName: "ebay_status", type: "string" },
          ],
        },
      }),

      // 8. Platform results aggregation
      n("8", "runCode", "Aggregate platform results", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate results from all platform price pushes
const platforms = [
  { name: "Google Merchant", status: vars.gmc_status || "unknown", id: vars.gmc_product_id || "" },
  { name: "Meta Catalog", status: vars.meta_batch_handle ? "submitted" : "failed", id: vars.meta_batch_handle || "" },
  { name: "Amazon SP-API", status: vars.amazon_status || "unknown", id: vars.amazon_submission_id || "" },
  { name: "eBay", status: vars.ebay_status || "unknown", id: vars.ebay_offer_id_result || "" },
];
const succeeded = platforms.filter(p => p.status !== "failed" && p.status !== "unknown" && p.status !== "").length;
const failed = platforms.length - succeeded;
const result = {
  platforms_succeeded: succeeded,
  platforms_failed: failed,
  platform_summary: platforms.map(p => p.name + ": " + p.status).join(" | "),
  all_succeeded: failed === 0 ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "platforms_succeeded", type: "number" },
            { name: "platforms_failed", type: "number" },
            { name: "platform_summary", type: "string" },
            { name: "all_succeeded", type: "string" },
          ],
        },
      }),

      // 9. Pricing ops Slack summary
      slackNode(
        "9",
        "Slack: Pricing propagation summary",
        1960,
        ":moneybag: Price Change Propagation\\nProduct: {product_title} ({product_id})\\nPrice: {old_price} -> {new_price} {currency} ({price_direction}, {change_percentage}%)\\nMAP: {map_price} | Compliant: {map_compliant}\\nPlatforms: {platforms_succeeded}/4 succeeded\\n{platform_summary}",
      ),

      // 10. Propagation audit
      sheetsNode(
        "10",
        "Log propagation audit to Sheets",
        2200,
        "PricePropagation",
        '[["{product_id}","{product_title}","{old_price}","{new_price}","{currency}","{price_direction}","{change_percentage}","{map_compliant}","{platforms_succeeded}","{platforms_failed}","{platform_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // approved -> Google Merchant
      e("3", "9", "output_2"), // not approved -> Slack alert (skip platforms)
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 462. PRICE DROP ALERT (8 nodes)
  // ===================================================================

  "Price Drop Alert": {
    summary:
      "Detects significant price drops and alerts subscribed customers. A frequent price scan detects drops above threshold via runCode, generates an AI significance note, matches affected subscribers, sends a price-drop email, logs conversions to Sheets, alerts monitoring ops on Slack, and updates the price baseline.",
    nodes: [
      trigger("Schedule: Frequent price scan", "schedule", {
        schedule: "0 */4 * * * (every 4 hours)",
        howItStarts:
          "A scheduled job scans product prices for significant drops compared to baseline and alerts subscribed customers who opted in to price-drop notifications",
        setup:
          "Configure price monitoring schedule. Maintain a price baseline Sheets or database. Set up subscriber matching by product. Define minimum drop threshold (e.g., 10%).",
        inputFields: [],
      }),

      // 2. Threshold detection (runCode)
      n("2", "runCode", "Detect price drops above threshold", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate price drop detection by comparing current vs baseline
// In production, this would read from a price baseline store
const threshold = 10; // minimum % drop to trigger alert
const sampleDrops = [
  { product_id: "prod_001", title: "Sample Product", old_price: 99.99, new_price: 79.99, image: "https://cdn.example.com/prod_001.jpg" }
];
const significantDrops = sampleDrops.filter(p => {
  const dropPct = ((p.old_price - p.new_price) / p.old_price) * 100;
  return dropPct >= threshold;
}).map(p => ({
  ...p,
  drop_pct: (((p.old_price - p.new_price) / p.old_price) * 100).toFixed(1),
  savings: (p.old_price - p.new_price).toFixed(2)
}));
const result = {
  drops_detected: significantDrops.length,
  drop_products: JSON.stringify(significantDrops),
  top_drop_title: significantDrops[0] ? significantDrops[0].title : "",
  top_drop_old: significantDrops[0] ? significantDrops[0].old_price.toFixed(2) : "0",
  top_drop_new: significantDrops[0] ? significantDrops[0].new_price.toFixed(2) : "0",
  top_drop_pct: significantDrops[0] ? significantDrops[0].drop_pct : "0",
  top_savings: significantDrops[0] ? significantDrops[0].savings : "0",
  scan_timestamp: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "drops_detected", type: "number" },
            { name: "drop_products", type: "string" },
            { name: "top_drop_title", type: "string" },
            { name: "top_drop_old", type: "string" },
            { name: "top_drop_new", type: "string" },
            { name: "top_drop_pct", type: "string" },
            { name: "top_savings", type: "string" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI significance note
      aiNode(
        "3",
        "AI assess price drop significance",
        520,
        "You are a pricing analyst. Given the detected price drops, assess their significance: is this a flash sale, clearance, competitor match, or regular markdown? Generate a brief customer-facing note explaining why this is a great deal. Respond JSON: {significance_type: string, customer_note: string, urgency_level: string, deal_score: number}",
        "Drops detected: {drops_detected} | Products: {drop_products} | Top drop: {top_drop_title} was ${top_drop_old} now ${top_drop_new} ({top_drop_pct}% off)",
        [
          {
            field: "significance_type",
            as: "significance_type",
            type: "string",
          },
          { field: "customer_note", as: "customer_note", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "deal_score", as: "deal_score", type: "number" },
        ],
      ),

      // 4. Subscriber matching
      n("4", "hitApi", "Match price-alert subscribers", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value: `[{"filters":[{"propertyName":"price_alert_opted_in","operator":"EQ","value":"true"}]}]`,
            },
            { key: "properties", value: '["email","firstname"]' },
            { key: "limit", value: "100" },
          ],
          variables: [
            { name: "total", customName: "subscriber_count", type: "number" },
            { name: "results", customName: "subscribers", type: "string" },
          ],
        },
      }),

      // 5. Price-drop email
      emailNode(
        "5",
        "Email: Price drop alert",
        1000,
        "{customer_email}",
        "Price Drop Alert: {top_drop_title} — Save {top_drop_pct}%!",
        "Hi there,\\n\\nA product you're watching just dropped in price!\\n\\n{top_drop_title}\\nWas: ${top_drop_old} → Now: ${top_drop_new}\\nYou save: ${top_savings} ({top_drop_pct}% off)\\n\\n{customer_note}\\n\\nUrgency: {urgency_level}\\n\\nShop now before the price goes back up!",
      ),

      // 6. Conversion logging
      sheetsNode(
        "6",
        "Log price drop alerts to Sheets",
        1240,
        "PriceDropAlerts",
        '[["{scan_timestamp}","{drops_detected}","{top_drop_title}","{top_drop_old}","{top_drop_new}","{top_drop_pct}","{significance_type}","{subscriber_count}","{deal_score}"]]',
      ),

      // 7. Monitoring ops Slack alert
      slackNode(
        "7",
        "Slack: Price drop monitoring alert",
        1480,
        ":chart_with_downwards_trend: Price Drop Scan Results\\nDrops detected: {drops_detected}\\nTop: {top_drop_title} — ${top_drop_old} -> ${top_drop_new} ({top_drop_pct}%)\\nType: {significance_type} | Urgency: {urgency_level} | Score: {deal_score}\\nSubscribers notified: {subscriber_count}\\nScan: {scan_timestamp}",
      ),

      // 8. Baseline update
      sheetsNode(
        "8",
        "Update price baseline",
        1720,
        "PriceBaseline",
        '[["{top_drop_title}","{top_drop_new}","{scan_timestamp}","baseline_updated"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 463. PRICE DROP ALERT CAMPAIGN (9 nodes)
  // ===================================================================

  "Price Drop Alert Campaign": {
    summary:
      "Runs a coordinated price-drop marketing campaign across email and SMS. A merchandising batch trigger pulls affected products and pricing, segments the audience via runCode, generates AI campaign copy, sends VIP early-access email, general campaign email, SMS for opted-in subscribers via Twilio, posts performance to Slack, and logs product-level data to Sheets.",
    nodes: [
      trigger("Webhook: Merchandising batch price drop", "webhook_app", {
        source: "PIM System",
        event: "batch.price_reduction",
        howItStarts:
          "The merchandising team executes a batch price reduction affecting multiple products, triggering a coordinated campaign across VIP early access, general audience, and SMS channels",
        setup:
          "Configure PIM or merchandising system webhook for batch price changes. Set up VIP segment in HubSpot. Prepare email and SMS templates.",
        inputFields: [],
      }),

      // 2. Affected products + pricing pull
      aiNode(
        "2",
        "AI extract batch price drop data",
        280,
        "You are a merchandising data parser. Extract the batch ID, affected product list (with IDs, titles, old prices, new prices), campaign name, start date, end date, and discount type (percentage, fixed, clearance) from the webhook payload. Calculate total savings and average discount. Respond JSON: {batch_id: string, products: string, product_count: number, campaign_name: string, start_date: string, end_date: string, discount_type: string, avg_discount_pct: string, total_products_value: string}",
        "{webhook_payload}",
        [
          { field: "batch_id", as: "batch_id", type: "string" },
          { field: "products", as: "drop_products", type: "string" },
          { field: "product_count", as: "product_count", type: "number" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "start_date", as: "campaign_start", type: "string" },
          { field: "end_date", as: "campaign_end", type: "string" },
          { field: "discount_type", as: "discount_type", type: "string" },
          { field: "avg_discount_pct", as: "avg_discount_pct", type: "string" },
          { field: "total_products_value", as: "total_value", type: "string" },
        ],
      ),

      // 3. Audience segmentation (runCode)
      n("3", "runCode", "Segment audience for campaign", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment audience into VIP (early access), general, and SMS-opted
const productCount = parseInt(vars.product_count || "0", 10);
const avgDiscount = parseFloat(vars.avg_discount_pct || "0");
const discountType = vars.discount_type || "percentage";
const isHighValue = avgDiscount >= 20 || discountType === "clearance";
const campaignUrgency = isHighValue ? "high" : avgDiscount >= 10 ? "medium" : "low";
const result = {
  vip_send: "yes",
  general_send: "yes",
  sms_send: isHighValue ? "yes" : "no",
  campaign_urgency: campaignUrgency,
  vip_early_access_hours: isHighValue ? "24" : "12",
  headline: vars.campaign_name || "Price Drop Alert",
  product_highlight: productCount + " products at up to " + avgDiscount + "% off",
  sms_eligible: isHighValue ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "vip_send", type: "string" },
            { name: "general_send", type: "string" },
            { name: "sms_send", type: "string" },
            { name: "campaign_urgency", type: "string" },
            { name: "vip_early_access_hours", type: "string" },
            { name: "headline", type: "string" },
            { name: "product_highlight", type: "string" },
            { name: "sms_eligible", type: "string" },
          ],
        },
      }),

      // 4. AI campaign copy generation
      aiNode(
        "4",
        "AI generate campaign copy",
        760,
        "You are a marketing copywriter for e-commerce price drop campaigns. Generate compelling email copy for VIP early access and general audience versions, plus a short SMS message. Include urgency, savings highlights, and clear CTAs. Respond JSON: {vip_subject: string, vip_body: string, general_subject: string, general_body: string, sms_message: string}",
        "Campaign: {headline} | Products: {product_highlight} | Urgency: {campaign_urgency} | Discount type: {discount_type} | Avg discount: {avg_discount_pct}% | VIP early access: {vip_early_access_hours}hrs | Ends: {campaign_end}",
        [
          { field: "vip_subject", as: "vip_subject", type: "string" },
          { field: "vip_body", as: "vip_body", type: "string" },
          { field: "general_subject", as: "general_subject", type: "string" },
          { field: "general_body", as: "general_body", type: "string" },
          { field: "sms_message", as: "sms_message", type: "string" },
        ],
      ),

      // 5. VIP early-access email
      emailNode(
        "5",
        "Email: VIP early-access campaign",
        1000,
        "{customer_email}",
        "{vip_subject}",
        "{vip_body}",
      ),

      // 6. General campaign email
      emailNode(
        "6",
        "Email: General price drop campaign",
        1240,
        "{customer_email}",
        "{general_subject}",
        "{general_body}",
      ),

      // 7. SMS for opted-in (Twilio)
      smsNode(
        "7",
        "Twilio: Price drop SMS campaign",
        1480,
        "{customer_phone}",
        "{sms_message}",
      ),

      // 8. Performance Slack post
      slackNode(
        "8",
        "Slack: Campaign performance post",
        1720,
        ":mega: Price Drop Campaign Launched\\nCampaign: {headline} | Batch: {batch_id}\\nProducts: {product_count} | Avg discount: {avg_discount_pct}%\\nType: {discount_type} | Urgency: {campaign_urgency}\\nVIP early access: {vip_early_access_hours}hrs\\nSMS eligible: {sms_eligible}\\nEnds: {campaign_end}",
      ),

      // 9. Product-level logging
      sheetsNode(
        "9",
        "Log campaign products to Sheets",
        1960,
        "PriceDropCampaigns",
        '[["{batch_id}","{campaign_name}","{product_count}","{avg_discount_pct}","{discount_type}","{campaign_urgency}","{campaign_start}","{campaign_end}","{sms_eligible}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 464. PRICE-SENSITIVE BROWSE ABANDONMENT (9 nodes)
  // ===================================================================

  "Price-Sensitive Browse Abandonment": {
    summary:
      "Recovers price-sensitive browse abandoners with BNPL options and guarantees. A browse abandon event with a price-sensitive flag triggers product and BNPL data pull, calculates installment options via runCode, routes on price tier, sends a high-ticket BNPL email or low-ticket guarantee email, delivers the recovery email, alerts on policy mismatches via Slack, and logs conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Browse abandonment (price-sensitive)", "webhook_app", {
        source: "Segment",
        event: "browse_abandoned.price_sensitive",
        howItStarts:
          "A visitor flagged as price-sensitive abandons browsing after viewing products but not adding to cart, likely deterred by price concerns",
        setup:
          "Configure analytics platform to detect price-sensitive browse abandonment (e.g., viewed product, checked price multiple times, compared prices). Include product ID and customer data.",
        inputFields: [],
      }),

      // 2. AI extraction + product pull
      aiNode(
        "2",
        "AI extract abandonment data",
        280,
        "You are a browse abandonment analyst. Parse the event to extract customer email, customer name, product ID, product title, product price, browse duration, pages viewed, price comparison signals, device type, and the price sensitivity score. Respond JSON: {customer_email: string, customer_name: string, product_id: string, product_title: string, product_price: string, browse_duration: string, pages_viewed: number, sensitivity_score: number, device_type: string, product_url: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_title", as: "product_title", type: "string" },
          { field: "product_price", as: "product_price", type: "string" },
          { field: "browse_duration", as: "browse_duration", type: "string" },
          { field: "pages_viewed", as: "pages_viewed", type: "number" },
          {
            field: "sensitivity_score",
            as: "sensitivity_score",
            type: "number",
          },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
        ],
      ),

      // 3. Installment calculation (runCode)
      n("3", "runCode", "Calculate BNPL installment options", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate Buy Now Pay Later installment options
const price = parseFloat(vars.product_price || "0");
const sensScore = parseInt(vars.sensitivity_score || "5", 10);
const isHighTicket = price >= 100;
const installments4 = (price / 4).toFixed(2);
const installments6 = (price / 6).toFixed(2);
const installments12 = (price / 12).toFixed(2);
const hasGuarantee = price < 200;
const priceTier = price >= 500 ? "premium" : price >= 100 ? "high" : price >= 50 ? "medium" : "low";
const result = {
  price_tier: priceTier,
  is_bnpl_eligible: isHighTicket ? "yes" : "no",
  monthly_4x: installments4,
  monthly_6x: installments6,
  monthly_12x: installments12,
  has_price_guarantee: hasGuarantee ? "yes" : "no",
  recovery_incentive: sensScore >= 7 ? "Free shipping on this order" : sensScore >= 5 ? "Price match guarantee" : "Satisfaction guaranteed",
  bnpl_provider: price >= 500 ? "Affirm" : "Afterpay",
  bnpl_message: isHighTicket ? "Pay as low as $" + installments4 + "/mo with " + (price >= 500 ? "Affirm" : "Afterpay") : "Price match guarantee — shop with confidence"
};
return result;`,
          outputVariables: [
            { name: "price_tier", type: "string" },
            { name: "is_bnpl_eligible", type: "string" },
            { name: "monthly_4x", type: "string" },
            { name: "monthly_6x", type: "string" },
            { name: "monthly_12x", type: "string" },
            { name: "has_price_guarantee", type: "string" },
            { name: "recovery_incentive", type: "string" },
            { name: "bnpl_provider", type: "string" },
            { name: "bnpl_message", type: "string" },
          ],
        },
      }),

      // 4. Price tier condition
      n("4", "condition", "High-ticket item?", 760, {
        conditionData: [
          {
            variable: "is_bnpl_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. High-ticket BNPL email (True path)
      emailNode(
        "5",
        "Email: BNPL financing option",
        1000,
        "{customer_email}",
        "Still Thinking About {product_title}? Pay Over Time!",
        "Hi {customer_name},\\n\\nWe noticed you were looking at {product_title} (${product_price}).\\n\\nGreat news — you can spread the cost with {bnpl_provider}!\\n\\n- 4 payments of ${monthly_4x}\\n- 6 payments of ${monthly_6x}\\n- 12 payments of ${monthly_12x}\\n\\nNo interest. No hidden fees. {recovery_incentive}.\\n\\nComplete your purchase: {product_url}",
      ),

      // 6. Low-ticket guarantee email (False path)
      emailNode(
        "6",
        "Email: Price guarantee recovery",
        1000,
        "{customer_email}",
        "Still Interested in {product_title}? We've Got You Covered",
        "Hi {customer_name},\\n\\nWe saw you checking out {product_title} (${product_price}).\\n\\n{bnpl_message}\\n\\n{recovery_incentive}\\n\\nShop with confidence: {product_url}",
        400,
      ),

      // 7. Recovery email confirmation
      n("7", "delayAction", "Wait for engagement window", 1240, {
        delayDetails: { delayType: "fixed", days: 0, hours: 2, minutes: 0 },
      }),

      // 8. Policy mismatch Slack alert
      slackNode(
        "8",
        "Slack: Recovery ops alert",
        1480,
        ":shopping_trolley: Price-Sensitive Recovery\\nCustomer: {customer_name} ({customer_email})\\nProduct: {product_title} at ${product_price}\\nTier: {price_tier} | BNPL: {is_bnpl_eligible} ({bnpl_provider})\\nSensitivity: {sensitivity_score}/10 | Browse: {browse_duration}\\nIncentive: {recovery_incentive}",
      ),

      // 9. Conversion logging
      sheetsNode(
        "9",
        "Log recovery attempt to Sheets",
        1720,
        "BrowseRecovery",
        '[["{customer_email}","{product_title}","{product_price}","{price_tier}","{is_bnpl_eligible}","{sensitivity_score}","{recovery_incentive}","{bnpl_provider}","{device_type}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high-ticket -> BNPL email
      e("4", "6", "output_2"), // low-ticket -> guarantee email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 465. PRICE-SENSITIVE CHURN RESCUE (9 nodes)
  // ===================================================================

  "Price-Sensitive Churn Rescue": {
    summary:
      "Rescues at-risk customers showing churn signals with value-tiered retention offers. A churn threshold trigger assembles a health profile, uses AI to calculate the optimal retention offer, routes on value tier, creates a VIP human outreach task in HubSpot, sends a standard rescue email, delivers an SMS touchpoint via Twilio, logs the rescue attempt to CRM, and feeds model performance data to Sheets.",
    nodes: [
      trigger("Webhook: Churn risk threshold crossed", "webhook_app", {
        source: "Customer Health Platform",
        event: "churn_risk.threshold_crossed",
        howItStarts:
          "A customer's churn risk score crosses the critical threshold, indicating they are likely to churn based on engagement decline, support tickets, or usage patterns",
        setup:
          "Configure customer health platform to fire webhook when churn risk exceeds threshold. Set up HubSpot for VIP task creation. Provide Twilio credentials for SMS.",
        inputFields: [],
      }),

      // 2. Health profile assembly
      aiNode(
        "2",
        "AI assemble churn health profile",
        280,
        "You are a customer health analyst. Parse the churn risk webhook to extract customer ID, customer email, customer name, customer phone, churn risk score (0-100), risk factors, lifetime value, subscription plan, last purchase date, support ticket count, engagement trend, and months as customer. Respond JSON: {customer_id: string, customer_email: string, customer_name: string, customer_phone: string, churn_score: number, risk_factors: string, lifetime_value: string, plan: string, last_purchase: string, ticket_count: number, engagement_trend: string, months_active: number}",
        "{webhook_payload}",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "churn_score", as: "churn_score", type: "number" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "lifetime_value", as: "lifetime_value", type: "string" },
          { field: "plan", as: "plan", type: "string" },
          { field: "last_purchase", as: "last_purchase", type: "string" },
          { field: "ticket_count", as: "ticket_count", type: "number" },
          { field: "engagement_trend", as: "engagement_trend", type: "string" },
          { field: "months_active", as: "months_active", type: "number" },
        ],
      ),

      // 3. AI retention offer calculation
      aiNode(
        "3",
        "AI calculate retention offer",
        520,
        "You are a retention strategist. Based on the customer's health profile, churn score, lifetime value, and risk factors, calculate the optimal retention offer. For high-LTV customers, suggest VIP concierge outreach. For standard customers, suggest discount or extended trial. For low-LTV, suggest feature education. Respond JSON: {value_tier: string, offer_type: string, offer_details: string, rescue_email_subject: string, rescue_email_body: string, estimated_save_probability: number, escalation_needed: string}",
        "Customer: {customer_name} | Churn score: {churn_score}/100 | LTV: {lifetime_value} | Plan: {plan} | Risk factors: {risk_factors} | Engagement: {engagement_trend} | Months: {months_active} | Tickets: {ticket_count}",
        [
          { field: "value_tier", as: "value_tier", type: "string" },
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "offer_details", as: "offer_details", type: "string" },
          {
            field: "rescue_email_subject",
            as: "rescue_subject",
            type: "string",
          },
          { field: "rescue_email_body", as: "rescue_body", type: "string" },
          {
            field: "estimated_save_probability",
            as: "save_probability",
            type: "number",
          },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),

      // 4. Value tier condition
      n("4", "condition", "VIP tier customer?", 760, {
        conditionData: [
          {
            variable: "value_tier",
            value: "vip",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. VIP human outreach task (HubSpot) — True path
      hubspotNode(
        "5",
        "HubSpot: Create VIP outreach task",
        1000,
        "POST",
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value: `{"hs_task_subject":"VIP Churn Rescue: {customer_name}","hs_task_body":"High-value customer at churn risk (score: {churn_score}/100). LTV: {lifetime_value}. Risk factors: {risk_factors}. Recommended offer: {offer_details}. Call within 24 hours.","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH","hs_task_type":"CALL"}`,
          },
        ],
        [{ name: "id", customName: "hubspot_task_id", type: "string" }],
      ),

      // 6. Standard rescue email — False path
      emailNode(
        "6",
        "Email: Churn rescue offer",
        1000,
        "{customer_email}",
        "{rescue_subject}",
        "{rescue_body}",
        400,
      ),

      // 7. SMS touchpoint (Twilio)
      smsNode(
        "7",
        "Twilio: Retention touchpoint SMS",
        1240,
        "{customer_phone}",
        "Hi {customer_name}, we miss you! {offer_details}. Questions? Reply to this message and our team will reach out personally.",
      ),

      // 8. CRM rescue logging (HubSpot)
      hubspotNode(
        "8",
        "Log rescue attempt to HubSpot",
        1480,
        "PATCH",
        "crm/v3/objects/contacts/{customer_id}",
        [
          {
            key: "properties",
            value: `{"churn_rescue_status":"attempted","churn_rescue_offer":"{offer_type}","churn_score_at_rescue":"{churn_score}","rescue_value_tier":"{value_tier}"}`,
          },
        ],
        [{ name: "id", customName: "contact_updated_id", type: "string" }],
      ),

      // 9. Model feedback
      sheetsNode(
        "9",
        "Log model feedback to Sheets",
        1720,
        "ChurnRescueFeedback",
        '[["{customer_id}","{customer_name}","{churn_score}","{value_tier}","{offer_type}","{save_probability}","{lifetime_value}","{risk_factors}","{engagement_trend}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // VIP -> human outreach task
      e("4", "6", "output_2"), // standard -> rescue email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 466. PRODUCT ANALYTICS EVENT TRACKER (8 nodes)
  // ===================================================================

  "Product Analytics Event Tracker": {
    summary:
      "Monitors product KPIs and alerts on anomalies with AI-powered root cause analysis. A frequent KPI poll triggers dynamic baseline comparison via runCode, evaluates anomaly severity via condition, fires a PagerDuty critical alert, sends Slack warnings, generates AI cause explanations, logs alerts to Sheets, and meta-alerts on monitor failure.",
    nodes: [
      trigger("Schedule: Product KPI poll", "schedule", {
        schedule: "*/15 * * * * (every 15 minutes)",
        howItStarts:
          "A scheduled job polls product analytics KPIs (conversion rate, add-to-cart rate, checkout abandonment, error rate) and compares against dynamic baselines to detect anomalies",
        setup:
          "Configure analytics API access (Google Analytics, Mixpanel, or similar). Define KPI thresholds and dynamic baseline windows. Set up PagerDuty for critical alerts.",
        inputFields: [],
      }),

      // 2. Dynamic baseline comparison (runCode)
      n("2", "runCode", "Compare KPIs against dynamic baseline", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current KPI values against dynamic baselines
// In production, baselines come from historical averages
const now = new Date();
const hour = now.getHours();
const dayOfWeek = now.getDay();
const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
const isPeak = hour >= 10 && hour <= 22;
// Simulated current values (in production, these come from analytics API)
const currentMetrics = {
  conversion_rate: 2.8,
  add_to_cart_rate: 8.5,
  checkout_abandon_rate: 68.0,
  error_rate: 0.5,
  page_load_ms: 1200,
  revenue_per_session: 3.20
};
// Dynamic baselines adjust for time of day and day of week
const baselineMult = isPeak ? 1.0 : 0.7;
const baselines = {
  conversion_rate: { expected: 3.2 * baselineMult, threshold_pct: 25 },
  add_to_cart_rate: { expected: 9.0 * baselineMult, threshold_pct: 20 },
  checkout_abandon_rate: { expected: 65.0, threshold_pct: 15 },
  error_rate: { expected: 0.2, threshold_pct: 150 },
  page_load_ms: { expected: 800, threshold_pct: 50 },
  revenue_per_session: { expected: 3.50 * baselineMult, threshold_pct: 30 }
};
const anomalies = [];
for (const [metric, baseline] of Object.entries(baselines)) {
  const current = currentMetrics[metric];
  const deviation = Math.abs(((current - baseline.expected) / baseline.expected) * 100);
  if (deviation > baseline.threshold_pct) {
    anomalies.push({ metric, current, expected: baseline.expected, deviation: deviation.toFixed(1), direction: current > baseline.expected ? "above" : "below" });
  }
}
const maxSeverity = anomalies.length === 0 ? "none" : anomalies.some(a => a.deviation > 50) ? "critical" : anomalies.some(a => a.deviation > 30) ? "warning" : "info";
const result = {
  anomaly_count: anomalies.length,
  anomalies_json: JSON.stringify(anomalies),
  max_severity: maxSeverity,
  top_anomaly: anomalies[0] ? anomalies[0].metric + " is " + anomalies[0].deviation + "% " + anomalies[0].direction + " baseline" : "No anomalies",
  poll_timestamp: now.toISOString(),
  is_peak: isPeak ? "yes" : "no",
  is_weekend: isWeekend ? "yes" : "no",
  monitor_healthy: "yes"
};
return result;`,
          outputVariables: [
            { name: "anomaly_count", type: "number" },
            { name: "anomalies_json", type: "string" },
            { name: "max_severity", type: "string" },
            { name: "top_anomaly", type: "string" },
            { name: "poll_timestamp", type: "string" },
            { name: "is_peak", type: "string" },
            { name: "is_weekend", type: "string" },
            { name: "monitor_healthy", type: "string" },
          ],
        },
      }),

      // 3. Anomaly severity condition
      n("3", "condition", "Critical anomaly detected?", 520, {
        conditionData: [
          {
            variable: "max_severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. PagerDuty critical alert (True path)
      pagerdutyNode(
        "4",
        "PagerDuty: Critical KPI alert",
        760,
        "CRITICAL: Product KPI anomaly — {top_anomaly}. Anomalies: {anomaly_count}. Peak hours: {is_peak}. Immediate investigation required.",
        "critical",
      ),

      // 5. Slack warning (False path)
      slackNode(
        "5",
        "Slack: KPI anomaly warning",
        760,
        ":warning: Product Analytics Anomaly\\nSeverity: {max_severity} | Anomalies: {anomaly_count}\\nTop: {top_anomaly}\\nPeak: {is_peak} | Weekend: {is_weekend}\\nTimestamp: {poll_timestamp}\\nDetails: {anomalies_json}",
        400,
      ),

      // 6. AI cause explanation
      aiNode(
        "6",
        "AI explain anomaly root cause",
        1000,
        "You are a product analytics expert. Given detected KPI anomalies, provide a brief root cause analysis. Consider time of day, day of week, recent deployments, marketing campaigns, and external factors. Suggest 2-3 investigation steps. Respond JSON: {root_cause_hypothesis: string, investigation_steps: string, confidence: string, impact_assessment: string}",
        "Anomalies: {anomalies_json} | Severity: {max_severity} | Peak hours: {is_peak} | Weekend: {is_weekend} | Timestamp: {poll_timestamp}",
        [
          { field: "root_cause_hypothesis", as: "root_cause", type: "string" },
          {
            field: "investigation_steps",
            as: "investigation_steps",
            type: "string",
          },
          { field: "confidence", as: "analysis_confidence", type: "string" },
          {
            field: "impact_assessment",
            as: "impact_assessment",
            type: "string",
          },
        ],
      ),

      // 7. Alert logging
      sheetsNode(
        "7",
        "Log KPI alerts to Sheets",
        1240,
        "ProductKPIAlerts",
        '[["{poll_timestamp}","{anomaly_count}","{max_severity}","{top_anomaly}","{is_peak}","{is_weekend}","{root_cause}","{investigation_steps}","{analysis_confidence}"]]',
      ),

      // 8. Meta-alert on monitor failure
      slackNode(
        "8",
        "Slack: Monitor health meta-alert",
        1480,
        ":stethoscope: KPI Monitor Health Check\\nStatus: {monitor_healthy}\\nLast poll: {poll_timestamp}\\nAnomalies processed: {anomaly_count}\\nSeverity: {max_severity}\\nRoot cause analysis: {root_cause}\\nImpact: {impact_assessment}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // critical -> PagerDuty
      e("3", "5", "output_2"), // not critical -> Slack warning
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 467. PRODUCT AVAILABILITY NOTIFIER (8 nodes)
  // ===================================================================

  "Product Availability Notifier": {
    summary:
      "Notifies waitlisted customers when products come back in stock with priority-based batching. A stock transition webhook triggers waitlist pull, routes on limited stock condition for batch sizing, sends back-in-stock email via Postmark, delivers priority SMS via Twilio for top waitlisters, logs conversions to Sheets, alerts inventory ops on Slack, and applies oversell protection.",
    nodes: [
      trigger("Webhook: Inventory stock transition", "webhook_app", {
        source: "Shopify",
        event: "inventory_levels/update",
        howItStarts:
          "An inventory level updates from zero to positive stock in Shopify, indicating a product is back in stock and waitlisted customers should be notified",
        setup:
          "Configure Shopify webhook for inventory_levels/update. Maintain a waitlist (in HubSpot lists or custom DB). Set up Postmark for back-in-stock transactional emails. Provide Twilio for priority SMS.",
        inputFields: [],
      }),

      // 2. AI extraction + stock validation
      aiNode(
        "2",
        "AI extract stock transition data",
        280,
        "You are an inventory data parser. Parse the inventory webhook to extract the inventory item ID, product variant ID, location ID, available quantity (new level), previous quantity, product title, product price, product URL, and SKU. Determine if this is a genuine restock (previous was 0 or near-0, now positive). Respond JSON: {inventory_item_id: string, variant_id: string, location_id: string, available_qty: number, previous_qty: number, product_title: string, product_price: string, product_url: string, sku: string, is_genuine_restock: string}",
        "{webhook_payload}",
        [
          {
            field: "inventory_item_id",
            as: "inventory_item_id",
            type: "string",
          },
          { field: "variant_id", as: "variant_id", type: "string" },
          { field: "available_qty", as: "available_qty", type: "number" },
          { field: "previous_qty", as: "previous_qty", type: "number" },
          { field: "product_title", as: "product_title", type: "string" },
          { field: "product_price", as: "product_price", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "sku", as: "sku", type: "string" },
          {
            field: "is_genuine_restock",
            as: "is_genuine_restock",
            type: "string",
          },
        ],
      ),

      // 3. Waitlist pull
      n("3", "hitApi", "Pull waitlist for restocked product", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value: `[{"filters":[{"propertyName":"waitlist_sku","operator":"EQ","value":"{sku}"}]}]`,
            },
            {
              key: "properties",
              value:
                '["email","firstname","phone","waitlist_priority","waitlist_date"]',
            },
            { key: "limit", value: "100" },
            {
              key: "sorts",
              value:
                '[{"propertyName":"waitlist_date","direction":"ASCENDING"}]',
            },
          ],
          variables: [
            { name: "total", customName: "waitlist_count", type: "number" },
            {
              name: "results",
              customName: "waitlist_contacts",
              type: "string",
            },
            {
              name: "results[0].properties.email",
              customName: "first_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.phone",
              customName: "first_phone",
              type: "string",
            },
          ],
        },
      }),

      // 4. Limited stock batching condition
      n("4", "condition", "Limited stock (under waitlist size)?", 760, {
        conditionData: [
          {
            variable: "available_qty",
            value: "{waitlist_count}",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Back-in-stock email via Postmark
      postmarkNode(
        "5",
        "Postmark: Back-in-stock email",
        1000,
        "{first_email}",
        "back-in-stock",
        `{"name":"{first_name}","product_title":"{product_title}","product_price":"{product_price}","product_url":"{product_url}","stock_qty":"{available_qty}","sku":"{sku}"}`,
        [
          { name: "MessageID", customName: "bis_msg_id", type: "string" },
          { name: "ErrorCode", customName: "bis_err", type: "number" },
        ],
      ),

      // 6. Priority SMS via Twilio (for top waitlisters)
      smsNode(
        "6",
        "Twilio: Priority back-in-stock SMS",
        1240,
        "{first_phone}",
        "Hi {first_name}! Great news — {product_title} is back in stock at ${product_price}! Only {available_qty} available. Shop now: {product_url}",
      ),

      // 7. Conversion logging
      sheetsNode(
        "7",
        "Log back-in-stock notifications to Sheets",
        1480,
        "BackInStockLog",
        '[["{sku}","{product_title}","{product_price}","{available_qty}","{previous_qty}","{waitlist_count}","{bis_msg_id}","{is_genuine_restock}"]]',
      ),

      // 8. Inventory ops Slack alert + oversell protection
      slackNode(
        "8",
        "Slack: Inventory ops restock alert",
        1720,
        ":package: Back-in-Stock Alert\\nProduct: {product_title} (SKU: {sku})\\nStock: {previous_qty} -> {available_qty} units\\nWaitlist: {waitlist_count} customers\\nStock vs Demand: {available_qty} available / {waitlist_count} waiting\\nGenuine restock: {is_genuine_restock}\\nPostmark: {bis_err}\\n:warning: Oversell risk: " +
          "{available_qty} < {waitlist_count} ? HIGH : LOW",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // limited stock -> batch notify (priority)
      e("4", "5", "output_2"), // enough stock -> notify all
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
